import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Dashboard,
  People,
  Assessment,
  CheckCircle,
  Cancel,
  Save,
  TrendingUp,
  TrendingDown,
  Schedule,
  ExpandMore,
  ExpandLess,
  VerifiedUser,
  Warning,
  Edit,
  Description,
} from '@mui/icons-material';
import samplesService, { Sample as SampleType, Priority, SampleStatus } from '../../services/samplesService';

interface Sample {
  id: string;
  sample_id: string;
  external_id?: string; // Client reference
  type_echantillon: string;
  statut: string;
  date_reception: string;
  date_prelevament: string;
  nom_projet?: string; // Project name
  site_minier?: string; // Mining site
  client: {
    company_name: string;
  };
  priorite: 'NORMALE' | 'URGENTE' | 'CRITIQUE';
  methodes_demandees?: string[];
  laboratoire_assigne?: string;
  metadonnees?: any;
  date_creation: string;
  date_modification: string;
}

interface SampleWithAnalyst extends Sample {
  analyst_name?: string;
}

interface LabRealTimeStatus {
  total_samples_in_lab: number;
  samples_prep_chimique: number;
  samples_analysis: number;
  samples_validation_pending: number;
  instruments_active: number;
  instruments_total: number;
  current_workload_percent: number;
  estimated_completion_hours: number;
  last_updated: string;
}

interface StaffPerformance {
  user_id: string;
  user_name: string;
  user_role: 'PREPARATION' | 'ANALYST';
  samples_completed_today: number;
  samples_completed_week: number;
  samples_completed_month: number;
  average_completion_time_hours: number;
  quality_score_percent: number;
  reanalysis_rate_percent: number;
  efficiency_trend: 'UP' | 'DOWN' | 'STABLE';
  last_activity: string;
}

interface QCSample {
  id: string;
  batch_id: string;
  qc_type: 'CRM' | 'LCS' | 'METHOD_BLANK' | 'REAGENT_BLANK' | 'DUPLICATE' | 'MATRIX_SPIKE';
  laboratory: string;
  reference_material_id?: string;
  certified_values?: any;
  measured_values: any;
  qc_results: any;
  acceptance_criteria: any;
  analysis_date: string;
  analyzed_by: string;
  instrument_id: string;
  passed: boolean;
  corrective_action?: string;
  notes?: string;
}

// 🔬 QC Correction interfaces
interface QCElementData {
  tq_average: number;
  tq_count: number;
  tq_rsd: number;
  certified_value?: number;
  tq_calculated?: number;      // TQ concentration after dilution formula
  correction_coefficient?: number;
  recovery_percent?: number;   // (TQ_calculated / Certified) × 100
}

interface BatchQCData {
  batchNumber: string;
  qcData: Record<string, QCElementData>;
  certifiedValues: Record<string, { value: number; unit: string }>;
  alerts: any[];
}

const SAASupervisorDashboard: React.FC = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [samples, setSamples] = useState<SampleWithAnalyst[]>([]);
  const [labStatus, setLabStatus] = useState<LabRealTimeStatus | null>(null);
  const [staffPerformance, setStaffPerformance] = useState<StaffPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationDialog, setValidationDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState<SampleWithAnalyst | null>(null);
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [qcSamples, setQcSamples] = useState<QCSample[]>([]);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  
  // 🔬 QC Correction state
  const [batchQCData, setBatchQCData] = useState<BatchQCData | null>(null);
  const [qcLoading, setQcLoading] = useState(false);
  const [correctedValues, setCorrectedValues] = useState<Record<string, Record<string, number>>>({});
  const [showCorrectedValues, setShowCorrectedValues] = useState(false);
  const [editedCertifiedValues, setEditedCertifiedValues] = useState<Record<string, number>>({});
  const [generatingPartialCert, setGeneratingPartialCert] = useState(false);
  const [editedConcentrations, setEditedConcentrations] = useState<Record<string, Record<string, { ppm: number; lecture: number }>>>({});
  const [editingCell, setEditingCell] = useState<{ sampleId: string; element: string } | null>(null);
  const [editInputValue, setEditInputValue] = useState('');

  // 🔧 ZMSM-only QA/QC (mirror of ICP Supervisor's QA/QC). State is reset every
  // time a new lot's validation modal opens (Layer A pattern) so values can never
  // leak from one lot to another. Card UI and payload conditioning are gated on
  // selectedSample.client?.company_name === 'ZMSM'; non-ZMSM SAA flow is unchanged.
  interface ZMSMQAQCStandard {
    customers_ref: string;
    lab_ref: string;
    [element: string]: string;
  }
  const [showZmsmQaqcForm, setShowZmsmQaqcForm] = useState<boolean>(false);
  const [zmsmQaqcStandards, setZmsmQaqcStandards] = useState<ZMSMQAQCStandard[]>([]);
  const [zmsmQaqcElements, setZmsmQaqcElements] = useState<string[]>([]);
  // 🆕 BIS-row autofill (prod-aligned 2026-05-15): the SAA Supervisor mirrors
  // the ICP Supervisor's "tick checkbox → autofill the BIS row from a sample's
  // analytical readings" UX. firstBISSampleId / secondBISSampleId track the
  // sample chosen for BIS 1 / BIS 2 respectively.
  const [firstSaaBISSampleId, setFirstSaaBISSampleId] = useState<string | null>(null);
  const [secondSaaBISSampleId, setSecondSaaBISSampleId] = useState<string | null>(null);

  const computeLectureFromPpm = (ppm: number, sample: any, element: string): number => {
    const prep = sample.metadonnees?.saa_preparation;
    const V = parseFloat(prep?.initialVolume || prep?.initial_volume || prep?.V || 100);
    const p = parseFloat(prep?.initialWeight || prep?.initial_weight || prep?.p || 2.5);
    let vf = V, vp = V, vf1 = V, vp1 = V;
    if (prep?.dilutions && Array.isArray(prep.dilutions) && prep.dilutions.length > 0) {
      for (const dil of prep.dilutions) {
        vp = parseFloat(dil.volumeTaken || dil.volume_taken || 10);
        vf = parseFloat(dil.finalVolume || dil.final_volume || 100);
      }
    }
    vf1 = parseFloat(prep?.vf1 || vf);
    vp1 = parseFloat(prep?.vp1 || vp);
    const defaultChoice = sample.metadonnees?.batch_analysis_summary?.dilution_choice ?? 1;
    const elementChoices = sample.metadonnees?.batch_analysis_summary?.element_dilution_choices || {};
    const dilutionChoice = elementChoices[element] ?? defaultChoice;
    if (dilutionChoice === 0) return ppm * (p / V);
    if (dilutionChoice === 2) return ppm * (p / V) * (vp1 / vf1);
    return ppm * (p / V) * (vp / vf);
  };

  const handleConcentrationEdit = (sampleId: string, element: string, currentPpm: number) => {
    setEditingCell({ sampleId, element });
    setEditInputValue(String(currentPpm));
  };

  const handleConcentrationInputChange = (e: React.ChangeEvent<HTMLInputElement>, sample: any, element: string) => {
    const val = e.target.value;
    setEditInputValue(val);
    const numVal = parseFloat(val);
    if (!isNaN(numVal) && numVal >= 0) {
      const newLecture = computeLectureFromPpm(numVal, sample, element);
      setEditedConcentrations(prev => ({
        ...prev,
        [sample.id]: { ...(prev[sample.id] || {}), [element]: { ppm: numVal, lecture: newLecture } }
      }));
    }
  };

  const handleConcentrationConfirm = () => {
    if (editingCell) {
      const val = parseFloat(editInputValue);
      if (isNaN(val) || val < 0) {
        setEditedConcentrations(prev => {
          const copy = { ...prev };
          if (copy[editingCell.sampleId]) {
            delete copy[editingCell.sampleId][editingCell.element];
            if (Object.keys(copy[editingCell.sampleId]).length === 0) delete copy[editingCell.sampleId];
          }
          return copy;
        });
      }
    }
    setEditingCell(null);
    setEditInputValue('');
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 10000); // Optimized: 10 seconds for general data
    return () => clearInterval(interval);
  }, []);

  // Add real-time updates for validation list
  useEffect(() => {
    // Poll for validation updates more frequently
    const validationInterval = setInterval(() => {
      if (currentTab === 2) { // Only poll when on validation tab
        loadValidationSamples();
      }
    }, 5000); // Optimized: 5 seconds for critical validation updates

    return () => clearInterval(validationInterval);
  }, [currentTab]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadLabRealTimeStatus(),
        loadStaffPerformance(),
        loadValidationSamples(),
        loadQualityControlData()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLabRealTimeStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/samples/laboratory-status/SAA', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLabStatus(data);
      } else {
        setLabStatus({
          total_samples_in_lab: 0,
          samples_prep_chimique: 0,
          samples_analysis: 0,
          samples_validation_pending: 0,
          instruments_active: 0,
          instruments_total: 0,
          current_workload_percent: 0,
          estimated_completion_hours: 0,
          last_updated: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error loading lab status:', error);
    }
  };

  const loadStaffPerformance = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/samples/staff-performance/SAA', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStaffPerformance(Array.isArray(data) ? data : []);
      } else {
        setStaffPerformance([]);
      }
    } catch (error) {
      console.error('Error loading staff performance:', error);
      setStaffPerformance([]);
    }
  };

  const loadValidationSamples = async () => {
    try {
      console.log('🔄 SAA Supervisor: Loading validation samples...');
      
      // Use samplesService instead of raw fetch to avoid JSON parsing issues
      // First get the total count, then fetch all if reasonable
      const countResponse = await samplesService.getSamples(1, 1, {
        status: [SampleStatus.VALIDATION_SERVICE]
      });
      const totalValidation = countResponse.total || 0;
      
      // Fetch all validation samples (up to 10000 for performance)
      const limit = Math.min(totalValidation, 10000);
      const response = await samplesService.getSamples(1, limit || 200, {
        status: [SampleStatus.VALIDATION_SERVICE]
      });
      
      const allSamples = response.samples || [];
      console.log(`📊 Found ${allSamples.length} total VALIDATION_SERVICE samples`);
      
      // Filter for SAA-related samples - ONLY show samples with actual SAA analysis
      const saaSamples = allSamples.filter((sample: any) => {
        const hasSaaAnalysis = sample.metadonnees?.saa_analysis_form || sample.metadonnees?.saa_analysis_data;
        
        // Regular SAA samples with SAA analysis data
        if (sample.statut === 'VALIDATION_SERVICE' && hasSaaAnalysis && 
            !sample.metadonnees?.multi_lab_workflow) {
          return true;
        }
        
        // Multi-lab samples with SAA elements that completed SAA analysis but not SAA validation
        const isMultiLab = sample.metadonnees?.multi_lab_workflow === true;
        if (isMultiLab && sample.statut === 'VALIDATION_SERVICE') {
          const selectedElements = sample.metadonnees?.selected_elements || [];
          const elementRouting = sample.metadonnees?.element_lab_routing || {};
          // 🔧 FIX: Check if ANY selected element routes to SAA (not just elementRouting values)
          const hasSaaElements = selectedElements.some((element: string) => 
            elementRouting[element] === 'LABORATOIRE_SAA'
          );
          // 🔧 FIX: Also check laboratories_involved as fallback
          const hasSaaInLabs = sample.laboratories_involved?.includes('LABORATOIRE_SAA') || false;
          const saaAnalysisDone = sample.metadonnees?.saa_analysis_completed === true;
          const saaValidationNotDone = sample.metadonnees?.saa_validation_completed !== true;
          return (hasSaaElements || hasSaaInLabs) && saaAnalysisDone && saaValidationNotDone;
        }
        
        return false;
      }) as any[];
      
      console.log(`🔬 Found ${saaSamples.length} SAA validation samples`);
      
      if (saaSamples.length > 0) {
        console.log('✅ SAA samples found:', saaSamples.map(s => ({
          sample_id: s.sample_id,
          batch_number: s.metadonnees?.batch_analysis_summary?.batch_number,
          is_batch_sample: s.metadonnees?.batch_analysis_summary?.is_batch_sample,
          has_saa_analysis: !!s.metadonnees?.saa_analysis_form
        })));
      }
      
      setSamples(saaSamples);
      
    } catch (error) {
      console.error('❌ Error loading validation samples:', error);
      setSamples([]);
    }
  };

  const loadQualityControlData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      try {
        const qcResponse = await fetch('/api/v1/samples/qc-samples?laboratory=SAA', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (qcResponse.ok) {
          const qcData = await qcResponse.json();
          setQcSamples(Array.isArray(qcData) ? qcData : []);
        } else {
          setQcSamples([]);
        }
      } catch (error) {
        console.log('QC samples API not available');
        setQcSamples([]);
      }
    } catch (error) {
      console.error('Error loading quality control data:', error);
      setQcSamples([]);
    }
  };

  const handleValidateSample = (sample: SampleWithAnalyst) => {
    setSelectedSample(sample);
    setValidationDialog(true);
    setValidationDecision('APPROVE');
    setValidationNotes('');
  };

  const handleGeneratePartialCertificate = async () => {
    try {
      setGeneratingPartialCert(true);
      const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];
      const sampleIds = batchSamples.map((s: any) => s.id);

      const token = localStorage.getItem('access_token');

      // Save any pending supervisor edits BEFORE generating the certificate
      // so the cert picks up corrected concentrations from the DB.
      if (Object.keys(editedConcentrations).length > 0) {
        for (const [sampleUuid, elements] of Object.entries(editedConcentrations)) {
          for (const [element, data] of Object.entries(elements)) {
            try {
              await fetch('/api/v1/saa/update-result', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sampleId: sampleUuid, element, newConcentrationPpm: (data as any).ppm }),
              });
            } catch (err) {
              console.error(`Failed to pre-save ${element} for ${sampleUuid} before cert generation:`, err);
            }
          }
        }
      }

      const response = await fetch('/api/v1/certificates/generate/partial-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sampleIds,
          laboratory: 'LABORATOIRE_SAA',
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to generate partial certificate');
      }

      const result = await response.json();
      const certificateId = result.data?.id;

      if (certificateId) {
        const downloadResponse = await fetch(`/api/v1/certificates/${certificateId}/download`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const html = await downloadResponse.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
        }
      }

      setSnackbarMessage(`Certificat partiel SAA genere avec succes pour ${sampleIds.length} echantillons`);
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(`Erreur: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setGeneratingPartialCert(false);
    }
  };

  const handleCompleteValidation = async () => {
    if (!selectedSample || !validationNotes.trim()) return;

    try {
      const token = localStorage.getItem('access_token');

      // Persist edited concentrations to the database before validation
      if (Object.keys(editedConcentrations).length > 0) {
        console.log('Saving edited concentrations to database...');
        for (const [sampleUuid, elements] of Object.entries(editedConcentrations)) {
          for (const [element, data] of Object.entries(elements)) {
            try {
              await fetch('/api/v1/saa/update-result', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sampleId: sampleUuid, element, newConcentrationPpm: data.ppm })
              });
              console.log(`Saved ${element} for ${sampleUuid}: ${data.ppm} ppm`);
            } catch (err) {
              console.error(`Failed to save ${element} for ${sampleUuid}:`, err);
            }
          }
        }
        console.log('All edited concentrations saved.');
        setEditedConcentrations({});
      }

      const isBatchValidation = (selectedSample as any)?.isBatchValidation;
      const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];
      
      if (isBatchValidation) {
        // Batch validation - validate all samples in the batch
        console.log(`🔄 Starting batch validation for ${batchSamples.length} samples`);
        
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];
        
        // 🚀 BATCH OPTIMIZATION: Concurrent validation calls instead of sequential
        console.log(`🔄 Batch validating ${batchSamples.length} SAA samples concurrently...`);
        
        const validationPromises = batchSamples.map(sample => 
          fetch(`/api/v1/samples/${sample.id}/validate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              decision: validationDecision,
              notes: `${validationNotes} [Validation de lot: ${getBatchNumber(selectedSample)}]`,
              validated_by: 'SAA Supervisor',
              laboratory: 'LABORATOIRE_SAA', // 🔥 CRITICAL FIX: Explicit laboratory
              batch_validation: true,
              batch_number: getBatchNumber(selectedSample),
              // 🔧 ZMSM-only: include QA/QC standards if loaded. Non-ZMSM lots get an empty
              // spread (no qaqc_data field), keeping the regular SAA flow byte-for-byte unchanged.
              ...(selectedSample?.client?.company_name === 'ZMSM' && showZmsmQaqcForm && zmsmQaqcStandards.length > 0 ? {
                qaqc_data: {
                  reference_standards: zmsmQaqcStandards,
                  batch_info: {
                    analyst_name: 'SAA Supervisor',
                    analysis_date: new Date().toISOString(),
                    // Layer C guard requires R-XXXX prefix; derive from sample_id.
                    batch_number: ((selectedSample?.sample_id || '').match(/^R-\d+/) || ['N/A'])[0],
                    sample_count: batchSamples.length,
                  }
                }
              } : {})
            })
          }).then(response => ({ sample, response }))
          .catch(error => ({ sample, error }))
        );

        // Wait for all validation calls to complete
        const results = await Promise.all(validationPromises);
        
        // Process results
        for (const result of results) {
          const { sample, response, error } = result;
          
          if (error) {
            errorCount++;
            errors.push(`${sample.sample_id}: Erreur de connexion`);
            console.error(`❌ Validation error for ${sample.sample_id}:`, error);
          } else if (response.ok) {
            successCount++;
            console.log(`✅ Sample ${sample.sample_id} validated successfully`);
          } else {
            errorCount++;
            let errorMessage = 'Erreur inconnue';
            
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || `Erreur ${response.status}`;
            } catch {
              errorMessage = `Erreur HTTP ${response.status}`;
            }
            
            errors.push(`${sample.sample_id}: ${errorMessage}`);
            console.error(`❌ Validation failed for ${sample.sample_id}: ${errorMessage}`);
          }
        }
        
        console.log(`✅ Batch validation completed: ${successCount} successful, ${errorCount} errors`);
        
        // Show results
        if (successCount === batchSamples.length) {
          setSnackbarMessage(`✅ Lot ${getBatchNumber(selectedSample)} ${validationDecision === 'APPROVE' ? 'approuvé' : 'rejeté'} avec succès - ${successCount} échantillons ${validationDecision === 'APPROVE' ? 'envoyés en génération de certificat' : 'renvoyés en analyse'}`);
        } else if (successCount > 0) {
          setSnackbarMessage(`⚠️ Validation partielle: ${successCount}/${batchSamples.length} échantillons validés. ${errorCount} erreurs.`);
        } else {
          setSnackbarMessage(`❌ Échec de la validation du lot: ${errors.join(', ')}`);
        }
        
      } else {
        // Individual sample validation
        const response = await fetch(`/api/v1/samples/${selectedSample.id}/validate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            decision: validationDecision,
            notes: validationNotes,
            validated_by: 'SAA Supervisor',
            laboratory: 'LABORATOIRE_SAA' // 🔥 CRITICAL FIX: Explicit laboratory
          })
        });

        if (response.ok) {
          const result = await response.json();
          setSnackbarMessage(`Échantillon ${selectedSample.sample_id} ${validationDecision === 'APPROVE' ? 'approuvé et envoyé en génération de certificat' : 'rejeté et renvoyé en analyse'} avec succès`);
          console.log('Sample validation completed:', result);
        } else {
          const errorData = await response.json();
          console.error('Validation failed:', errorData);
          setSnackbarMessage(`Erreur lors de la validation: ${errorData.message || 'Erreur inconnue'}`);
          setSnackbarOpen(true);
          return;
        }
      }
      
      setSnackbarOpen(true);
      setValidationDialog(false);
      setSelectedSample(null);
      setValidationNotes('');
      setValidationDecision('APPROVE');
      
      // Reload the validation samples list to remove the validated samples
      await loadValidationSamples();
      
    } catch (error) {
      console.error('Error validating samples:', error);
      setSnackbarMessage('Erreur lors de la validation - Vérifiez votre connexion');
      setSnackbarOpen(true);
    }
  };

  const getPriorityColor = (priority: 'NORMALE' | 'URGENTE' | 'CRITIQUE') => {
    switch (priority) {
      case 'CRITIQUE': return 'error';
      case 'URGENTE': return 'warning';
      default: return 'primary';
    }
  };

  // Helper functions for batch processing
  const groupSamplesByBatch = (samples: SampleWithAnalyst[]) => {
    const batches: Record<string, SampleWithAnalyst[]> = {};
    const individualSamples: SampleWithAnalyst[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
      if (batchInfo && (batchInfo.is_batch_sample || batchInfo.batch_number)) {
        const batchNumber = batchInfo.batch_number;
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: SampleWithAnalyst): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true || 
           sample.metadonnees?.batch_analysis_summary?.batch_number !== undefined;
  };

  const getBatchNumber = (sample: SampleWithAnalyst): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || 
           sample.metadonnees?.batch_analysis_summary?.batch_number || null;
  };

  const getBatchPosition = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  // 🔬 Load QC data for a batch
  const loadBatchQCData = async (batchNumber: string) => {
    setQcLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch QC data for this batch
      const qcResponse = await fetch(`/api/v1/saa-qc/batch/${encodeURIComponent(batchNumber)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch certified values
      const certifiedResponse = await fetch('/api/v1/saa-qc/certified-values', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let qcData: Record<string, QCElementData> = {};
      let certifiedValues: Record<string, { value: number; unit: string }> = {};
      
      if (qcResponse.ok) {
        const data = await qcResponse.json();
        // 🔄 Backend now returns qcSummary at top level
        qcData = data.qcSummary || data.data?.qc_summary || {};
        console.log('🔬 QC data from API:', data, 'Parsed qcData:', qcData);
      } else {
        console.warn('⚠️ QC API response not OK:', qcResponse.status, qcResponse.statusText);
      }
      
      if (certifiedResponse.ok) {
        const data = await certifiedResponse.json();
        // 🔄 Backend now returns certifiedValues at top level
        certifiedValues = data.certifiedValues || data.data || {};
        console.log('🔬 Certified values from API:', certifiedValues);
      } else {
        console.warn('⚠️ Certified values API response not OK:', certifiedResponse.status);
      }
      
      // 🔬 Calculate correction coefficients using proper dilution formula
      // Formula: TQ_calculated = tq_reading × (V/p) × (VD/PD) [÷ 10000 for %]
      // Coefficient = Certified_Value / TQ_calculated
      
      // Default dilution parameters (standard SAA method)
      const V = 200;   // volume ml
      const p = 0.25;  // mass g
      const baseFactor = V / p; // = 800
      
      // Element-specific VD factors (from standard SAA methods)
      // For ppm elements: VD=1, for % elements: VD=100
      const elementVDFactors: Record<string, number> = {
        'Ag': 1, 'Au': 1, 'Cd': 1, 'Co': 1, 'Cu': 1, 'Mn': 1, 'Ni': 1,  // ppm elements
        'Pb': 100, 'Zn': 100, 'Fe': 100,  // % elements (high concentration)
      };
      
      Object.keys(qcData).forEach(element => {
        const elementQC = qcData[element];
        const certified = certifiedValues[element];
        if (certified && elementQC.tq_average > 0) {
          elementQC.certified_value = certified.value;
          
          // Get VD factor for this element (default to 1 if not specified)
          const VD = elementVDFactors[element] || 1;
          const PD = 1;
          
          // Calculate TQ concentration using dilution formula
          let tqCalculated = elementQC.tq_average * baseFactor * (VD / PD);
          
          // For % units, convert ppm to % by dividing by 10000
          if (certified.unit === '%') {
            tqCalculated = tqCalculated / 10000;
          }
          
          // Store calculated values
          elementQC.tq_calculated = Math.round(tqCalculated * 10000) / 10000;
          elementQC.correction_coefficient = Math.round((certified.value / tqCalculated) * 10000) / 10000;
          elementQC.recovery_percent = Math.round((tqCalculated / certified.value) * 100 * 100) / 100;
          
          console.log(`🔬 ${element}: TQ_avg=${elementQC.tq_average}, VD=${VD}, TQ_calc=${tqCalculated.toFixed(4)}, Certified=${certified.value}${certified.unit}, Coeff=${elementQC.correction_coefficient}, Recovery=${elementQC.recovery_percent}%`);
        }
      });
      
      setBatchQCData({
        batchNumber,
        qcData,
        certifiedValues,
        alerts: []
      });
      
      console.log('🔬 Loaded QC data for batch:', batchNumber, qcData);
      
    } catch (error) {
      console.error('Error loading QC data:', error);
      setBatchQCData(null);
    } finally {
      setQcLoading(false);
    }
  };

  // 🔬 Handle certified value edit - recalculates coefficient and recovery
  const handleCertifiedValueChange = (element: string, newValue: number) => {
    if (!batchQCData || !newValue || newValue <= 0) return;
    
    const elementQC = batchQCData.qcData[element];
    if (!elementQC || !elementQC.tq_calculated || elementQC.tq_calculated <= 0) return;
    
    const tqCalculated = elementQC.tq_calculated;
    
    // Calculate new coefficient and recovery with new certified value
    const newCoefficient = Math.round((newValue / tqCalculated) * 10000) / 10000;
    const newRecovery = Math.round((tqCalculated / newValue) * 100 * 100) / 100;
    
    console.log(`🔬 ${element}: Certified value changed to ${newValue}, New Coeff=${newCoefficient}, Recovery=${newRecovery}%`);
    
    // Update edited values state
    setEditedCertifiedValues(prev => ({
      ...prev,
      [element]: newValue
    }));
    
    // 🔥 Create a completely new state object to ensure React re-renders
    const newQCData: Record<string, QCElementData> = {};
    Object.entries(batchQCData.qcData).forEach(([el, qc]) => {
      if (el === element) {
        // Create new object with updated values for this element
        newQCData[el] = {
          ...qc,
          certified_value: newValue,
          correction_coefficient: newCoefficient,
          recovery_percent: newRecovery
        };
      } else {
        // Keep other elements as-is (but still new object reference)
        newQCData[el] = { ...qc };
      }
    });
    
    // Update state with recalculated values - NEW OBJECTS ensure re-render
    setBatchQCData({
      ...batchQCData,
      qcData: newQCData,
      certifiedValues: {
        ...batchQCData.certifiedValues,
        [element]: { 
          value: newValue, 
          unit: batchQCData.certifiedValues[element]?.unit || 'ppm' 
        }
      }
    });
  };

  // 🔬 Apply QC correction to a sample's element
  const applyCorrection = (sampleId: string, element: string, originalValue: number, coefficient: number) => {
    const correctedValue = originalValue * coefficient;
    setCorrectedValues(prev => ({
      ...prev,
      [sampleId]: {
        ...(prev[sampleId] || {}),
        [element]: correctedValue
      }
    }));
    setSnackbarMessage(`✅ Correction appliquée: ${element} pour ${sampleId} (${originalValue.toFixed(4)} → ${correctedValue.toFixed(4)} ppm)`);
    setSnackbarOpen(true);
  };

  // 🔬 Apply correction to all samples for an element - NOW SAVES TO DATABASE!
  const applyElementCorrection = async (element: string, coefficient: number, batchSamples: any[]) => {
    const batchNumber = batchQCData?.batchNumber || getBatchNumber(selectedSample);
    
    if (!batchNumber) {
      setSnackbarMessage(`❌ Erreur: Numéro de lot introuvable`);
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      
      // 🔥 CALL BACKEND API TO PERSIST CORRECTIONS TO DATABASE
      const response = await fetch(`/api/v1/saa-qc/batch/${encodeURIComponent(batchNumber)}/apply-correction/element`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          element,
          coefficient
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`✅ Correction saved to database:`, result);
      
      // Also update local state for immediate UI feedback
      const newCorrectedValues = { ...correctedValues };
      let count = 0;
      
      batchSamples.forEach(sample => {
        const analysisData = sample.metadonnees?.saa_analysis_form || sample.metadonnees?.batch_analysis_data;
        if (analysisData?.analysis_readings) {
          const elementReading = analysisData.analysis_readings.find(
            (r: any) => r.element?.toUpperCase() === element.toUpperCase()
          );
          if (elementReading) {
            const originalPpm = elementReading.concentration_ppm || 0;
            if (originalPpm > 0) {
              if (!newCorrectedValues[sample.id]) {
                newCorrectedValues[sample.id] = {};
              }
              newCorrectedValues[sample.id][element] = originalPpm * coefficient;
              count++;
            }
          }
        }
      });
      
      setCorrectedValues(newCorrectedValues);
      setShowCorrectedValues(true); // Auto-enable to show corrected values
      
      setSnackbarMessage(`✅ Correction ${element} enregistrée: ${result.updatedCount || count} échantillons corrigés (coef: ${coefficient.toFixed(4)}) - Sauvegardé en base de données!`);
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error('❌ Error applying correction:', error);
      setSnackbarMessage(`❌ Erreur lors de l'application de la correction: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleValidateBatch = async (batchSamples: SampleWithAnalyst[]) => {
    // For batch validation, we'll set the first sample as selectedSample
    // but we'll also store the entire batch for display
    if (batchSamples.length > 0) {
      setSelectedSample({
        ...batchSamples[0],
        // Add batch context to the sample
        batchSamples: batchSamples,
        isBatchValidation: true
      } as any);
      // Reset ZMSM QA/QC state so values from a previously-opened lot can never leak in.
      setShowZmsmQaqcForm(false);
      setZmsmQaqcStandards([]);
      setZmsmQaqcElements([]);
      setFirstSaaBISSampleId(null);
      setSecondSaaBISSampleId(null);
      setValidationDialog(true);
      setValidationDecision('APPROVE');
      setValidationNotes('');
      setCorrectedValues({});
      setShowCorrectedValues(false);
      
      // 🔬 Load QC data for this batch
      const batchNumber = batchSamples[0]?.metadonnees?.batch_info?.batch_number || 
                          batchSamples[0]?.metadonnees?.batch_analysis_summary?.batch_number;
      if (batchNumber) {
        await loadBatchQCData(batchNumber);
      }
    }
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  // ✅ CRITICAL FIX: Consistent element ordering for SAA batch validation
  // 🔥 ENHANCED: For multi-lab samples, only show elements routed to SAA lab
  // 🔧 FIX: Get elements for a SPECIFIC sample (for greyed field detection)
  // 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
  const getSampleSpecificElements = (sample: any): string[] => {
    if (!sample) return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
    
    // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
    // This ensures elements removed per-sample are properly greyed out
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLab = sample.metadonnees?.multi_lab_workflow === true ||
                       Object.keys(elementRouting).length > 0;
    
    if (isMultiLab && selectedElements.length > 0) {
      // Filter selected_elements to only those routed to SAA lab
      const saaElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_SAA'
      );
      if (saaElements.length > 0) {
        console.log(`🔍 SAA Supervisor: Elements for ${sample.sample_id} (filtered by selected_elements):`, saaElements);
        return saaElements;
      }
    }
    
    // 2. Direct selected_elements array (for non-multi-lab samples)
    if (selectedElements.length > 0) {
      const validSaaElements = ['Ag', 'Au', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
      const saaElements = selectedElements.filter((el: string) => 
        validSaaElements.includes(el)
      );
      if (saaElements.length > 0) return saaElements;
      return selectedElements;
    }
    
    // 3. Fallback to method mapping
    if (sample.metadonnees?.analyses_demandees && Array.isArray(sample.metadonnees.analyses_demandees)) {
      const methodToElements: {[key: string]: string[]} = {
        'AA-PRE.MO/ANA/002': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
        'SAA': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
        'SAA-MULTI': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
      };
      const elements = new Set<string>();
      sample.metadonnees.analyses_demandees.forEach((method: string) => {
        const methodElements = methodToElements[method] || ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
        methodElements.forEach(el => elements.add(el));
      });
      if (elements.size > 0) return Array.from(elements);
    }
    
    return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
  };

  // 🔧 FIX: Get ALL unique elements across entire batch (union of all samples)
  const getSAABatchElementOrder = (samples: any[]): string[] => {
    if (!samples || samples.length === 0) return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
    
    const allElements = new Set<string>();
    samples.forEach(sample => {
      const sampleElements = getSampleSpecificElements(sample);
      sampleElements.forEach(el => allElements.add(el));
    });
    
    // Sort: standard SAA order first, then others
    const standardOrder = ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
    const result = Array.from(allElements).sort((a, b) => {
      const aIndex = standardOrder.indexOf(a);
      const bIndex = standardOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    return result;
  };

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Dashboard />} label="Vue d'Ensemble SAA" />
          <Tab icon={<People />} label="Performance Équipe" />
          <Tab icon={<Assessment />} label="Validation Échantillons" />
        </Tabs>
      </Paper>

      {/* Vue d'Ensemble SAA Tab */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            <Dashboard sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tableau de Bord SAA - Supervision en Temps Réel
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.primary.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>Échantillons en Laboratoire</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.total_samples_in_lab || 0}
                  </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total dans SAA
                  </Typography>
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      🧪
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.warning.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>En Validation</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.samples_validation_pending || 0}
                  </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Attente validation
                  </Typography>
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      ⏳
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.success.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>Instruments Actifs</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.instruments_active || 0}/{labStatus?.instruments_total || 0}
                  </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Opérationnels
                  </Typography>
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      ⚡
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.info.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.info.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>Charge de Travail</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.current_workload_percent || 0}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={labStatus?.current_workload_percent || 0} 
                        sx={{ 
                          mt: 1,
                          background: `linear-gradient(90deg, ${alpha(theme.palette.info.main, 0.2)}, ${alpha(theme.palette.info.main, 0.1)})`,
                          '& .MuiLinearProgress-bar': {
                            background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      📊
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Performance Équipe Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <People sx={{ mr: 1, verticalAlign: 'middle' }} />
            Performance de l'Équipe SAA
          </Typography>

          {Array.isArray(staffPerformance) && staffPerformance.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Rôle</TableCell>
                    <TableCell align="right">Aujourd'hui</TableCell>
                    <TableCell align="right">Cette Semaine</TableCell>
                    <TableCell align="right">Ce Mois</TableCell>
                    <TableCell align="right">Temps Moyen</TableCell>
                    <TableCell align="right">Score Qualité</TableCell>
                    <TableCell align="center">Tendance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffPerformance.map((staff) => (
                    <TableRow key={staff.user_id}>
                      <TableCell>{staff.user_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={staff.user_role} 
                          size="small" 
                          color={staff.user_role === 'ANALYST' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="right">{staff.samples_completed_today}</TableCell>
                      <TableCell align="right">{staff.samples_completed_week}</TableCell>
                      <TableCell align="right">{staff.samples_completed_month}</TableCell>
                      <TableCell align="right">{staff.average_completion_time_hours.toFixed(1)}h</TableCell>
                      <TableCell align="right">
                        <Typography 
                          color={staff.quality_score_percent >= 95 ? 'success.main' : 'warning.main'}
                          fontWeight={600}
                        >
                          {staff.quality_score_percent.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {staff.efficiency_trend === 'UP' && <TrendingUp color="success" />}
                        {staff.efficiency_trend === 'DOWN' && <TrendingDown color="error" />}
                        {staff.efficiency_trend === 'STABLE' && <Schedule color="info" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              Aucune donnée de performance disponible pour l'équipe SAA
            </Alert>
          )}
        </Box>
      )}

      {/* Validation Échantillons Tab */}
      {currentTab === 2 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Validation des Échantillons SAA
          </Typography>

          {Array.isArray(samples) && samples.length > 0 ? (
            <Box>
              {(() => {
                const { batches, individualSamples } = groupSamplesByBatch(samples);
                const hasBatches = Object.keys(batches).length > 0;
                const hasIndividual = individualSamples.length > 0;

                return (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>N° Échantillon / Lot</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>Analyste</TableCell>
                          <TableCell>Date Analyse</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Render Batch Samples */}
                        {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                          const totalWeight = batchSamples.reduce((sum, sample) => {
                            const weight = sample.metadonnees?.saa_preparation?.initialWeight || 0;
                            return sum + weight;
                          }, 0);

                          // ✅ NOTE: This is just for display purposes in batch header, not for column generation
                          const elementsAnalyzed = batchSamples[0]?.metadonnees?.batch_analysis_summary?.elements_analyzed || [];
                          const belowLODCount = batchSamples[0]?.metadonnees?.batch_analysis_summary?.below_lod_count || 0;
                          const isExpanded = expandedBatches.has(batchNumber);

                          // Multi-lab pending/validated badge for batch header
                          const _batchMeta = batchSamples[0]?.metadonnees || {};
                          const _batchLabs = [...new Set(Object.values(_batchMeta.element_lab_routing || {}) as string[])];
                          const _batchPending: string[] = [];
                          const _batchValidated: string[] = [];
                          if (_batchLabs.length >= 2) {
                            for (const _bl of _batchLabs) {
                              if (_bl === 'LABORATOIRE_SAA') continue;
                              if (_bl.includes('ICP')) {
                                _batchMeta.icp_validation_completed === true ? _batchValidated.push('ICP') : _batchPending.push('ICP');
                              } else if (_bl.includes('AU')) {
                                _batchMeta.au_validation_completed === true ? _batchValidated.push('AU') : _batchPending.push('AU');
                              } else if (_bl.includes('PHYSICO')) {
                                _batchMeta.physico_chimique_validated === true ? _batchValidated.push('Physico-Chimique') : _batchPending.push('Physico-Chimique');
                              } else if (_bl.includes('VOLUMETRIE')) {
                                _batchMeta.volumetrie_validation_completed === true ? _batchValidated.push('Volumétrie') : _batchPending.push('Volumétrie');
                              }
                            }
                          }

                          return (
                            <React.Fragment key={batchNumber}>
                              {/* Batch Header Row - Now Clickable */}
                              <TableRow 
                                sx={{ 
                                  backgroundColor: '#e3f2fd',
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: '#bbdefb' }
                                }}
                                onClick={() => toggleBatchExpansion(batchNumber)}
                              >
                                <TableCell colSpan={8}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={2}>
                                      <IconButton size="small" sx={{ p: 0 }}>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                      <Chip 
                                        label="LOT SAA VALIDATION" 
                                        color="primary" 
                                        variant="filled"
                                        sx={{ 
                                          fontWeight: 'bold',
                                          backgroundColor: '#1976d2',
                                          color: 'white'
                                        }}
                                      />
                                      <Typography variant="h6" fontWeight="bold" color="primary">
                                        {batchNumber}
                                      </Typography>
                                      <Chip 
                                        label={`${batchSamples.length} échantillons`} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                      />
                                      <Chip 
                                        label={`${totalWeight.toFixed(2)} g total`} 
                                        size="small" 
                                        color="info" 
                                        variant="outlined"
                                      />
                                      {elementsAnalyzed.length > 0 && (
                                        <Box display="flex" gap={0.5}>
                                          {elementsAnalyzed.slice(0, 4).map((element: string) => (
                                            <Chip key={element} label={element} size="small" sx={{ fontSize: '0.7rem' }} />
                                          ))}
                                          {elementsAnalyzed.length > 4 && (
                                            <Chip label={`+${elementsAnalyzed.length - 4}`} size="small" />
                                          )}
                                        </Box>
                                      )}
                                      {belowLODCount > 0 && (
                                        <Chip 
                                          label={`⚠️ ${belowLODCount} < LOD`} 
                                          size="small" 
                                          color="error"
                                        />
                                      )}
                                      {_batchPending.length > 0 && (
                                        <Chip
                                          label={`⏳ En attente: ${_batchPending.join(', ')}`}
                                          size="small"
                                          sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ff9800' }}
                                        />
                                      )}
                                      {_batchValidated.length > 0 && (
                                        <Chip
                                          label={`✅ Validé: ${_batchValidated.join(', ')}`}
                                          size="small"
                                          sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #4caf50' }}
                                        />
                                      )}
                                    </Box>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<VerifiedUser />}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent batch expansion when clicking button
                                        handleValidateBatch(batchSamples);
                                      }}
                                      color="primary"
                                      sx={{ fontWeight: 'bold' }}
                                    >
                                      Valider le Lot
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Individual Batch Sample Rows - Only show when expanded */}
                              {isExpanded && batchSamples.map((sample, index) => {
                                // Multi-lab Statut badge logic (mirrors prod _p2/_v2/_m2 block)
                                const _m2: any = sample.metadonnees || {};
                                const _ml2 = _m2.multi_lab_workflow === true || _m2.bgm_method_selected === true;
                                const _labs2: string[] = _m2.laboratories_involved || (sample as any).laboratories_involved || [];
                                const _p2: string[] = [];
                                const _v2: string[] = [];
                                if (_ml2) {
                                  for (const _l2 of _labs2) {
                                    if (_l2 === 'LABORATOIRE_SAA') continue;
                                    if (_l2.includes('ICP')) {
                                      _m2.icp_validation_completed === true ? _v2.push('ICP') : _p2.push('ICP');
                                    } else if (_l2.includes('AU')) {
                                      _m2.au_validation_completed === true ? _v2.push('AU') : _p2.push('AU');
                                    } else if (_l2.includes('PHYSICO')) {
                                      _m2.physico_chimique_validated === true ? _v2.push('Physico-Chimique') : _p2.push('Physico-Chimique');
                                    } else if (_l2.includes('VOLUMETRIE')) {
                                      _m2.volumetrie_validation_completed === true ? _v2.push('Volumétrie') : _p2.push('Volumétrie');
                                    }
                                  }
                                }
                                const _sv = _m2.saa_validation_completed === true;
                                return (
                                <TableRow 
                                  key={sample.id}
                                  sx={{ 
                                    backgroundColor: '#f8f9ff',
                                    '&:hover': { backgroundColor: '#f0f4ff' }
                                  }}
                                >
                                  <TableCell sx={{ pl: 4 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {sample.sample_id}
                                      </Typography>
                                      <Chip 
                                        label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>{sample.type_echantillon}</TableCell>
                                  <TableCell>{sample.client?.company_name}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.priorite} 
                                      size="small" 
                                      color={getPriorityColor(sample.priorite)}
                                    />
                                  </TableCell>
                                  <TableCell>{sample.analyst_name || sample.metadonnees?.saa_analyst_name || 'SAA Analyst'}</TableCell>
                                  <TableCell>{new Date(sample.date_modification).toLocaleDateString('fr-FR')}</TableCell>
                                  <TableCell>
                                    {!_ml2 ? (
                                      <Chip label={sample.statut} size="small" color="info" />
                                    ) : (
                                      <Box display="flex" flexDirection="column" gap={0.5}>
                                        <Chip
                                          label={_sv ? 'SAA Validé' : sample.statut}
                                          size="small"
                                          color={_sv ? 'success' : 'info'}
                                        />
                                        {_p2.length > 0 && (
                                          <Chip
                                            label={`⏳ Attente: ${_p2.join(', ')}`}
                                            size="small"
                                            sx={{ fontWeight: 'bold', fontSize: '0.65rem', backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ff9800' }}
                                          />
                                        )}
                                        {_v2.length > 0 && (
                                          <Chip
                                            label={`✅ Validé: ${_v2.join(', ')}`}
                                            size="small"
                                            sx={{ fontWeight: 'bold', fontSize: '0.65rem', backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #4caf50' }}
                                          />
                                        )}
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<CheckCircle />}
                                      onClick={() => handleValidateSample(sample)}
                                      color="primary"
                                    >
                                      Individuel
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}

                        {/* Render Individual Samples */}
                        {individualSamples.map((sample) => {
                          const _iMeta = sample.metadonnees || {};
                          const _iLabs = [...new Set(Object.values(_iMeta.element_lab_routing || {}) as string[])];
                          const _iPending: string[] = [];
                          const _iValidated: string[] = [];
                          if (_iLabs.length >= 2) {
                            for (const _il of _iLabs) {
                              if (_il === 'LABORATOIRE_SAA') continue;
                              if (_il.includes('ICP')) {
                                _iMeta.icp_validation_completed === true ? _iValidated.push('ICP') : _iPending.push('ICP');
                              } else if (_il.includes('AU')) {
                                _iMeta.au_validation_completed === true ? _iValidated.push('AU') : _iPending.push('AU');
                              } else if (_il.includes('PHYSICO')) {
                                _iMeta.physico_chimique_validated === true ? _iValidated.push('Physico-Chimique') : _iPending.push('Physico-Chimique');
                              } else if (_il.includes('VOLUMETRIE')) {
                                _iMeta.volumetrie_validation_completed === true ? _iValidated.push('Volumétrie') : _iPending.push('Volumétrie');
                              }
                            }
                          }
                          return (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <Box>
                                <Typography fontWeight={600}>{sample.sample_id}</Typography>
                                {_iPending.length > 0 && (
                                  <Chip label={`En attente: ${_iPending.join(', ')}`} size="small" color="warning"
                                    sx={{ mt: 0.5, fontSize: '0.7rem' }} />
                                )}
                                {_iValidated.length > 0 && (
                                  <Chip label={`✅ Validé: ${_iValidated.join(', ')}`} size="small"
                                    sx={{ mt: 0.5, fontSize: '0.7rem', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{sample.type_echantillon}</TableCell>
                            <TableCell>{sample.client?.company_name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.priorite} 
                                size="small" 
                                color={getPriorityColor(sample.priorite)}
                              />
                            </TableCell>
                            <TableCell>{sample.analyst_name || sample.metadonnees?.saa_analyst_name || 'SAA Analyst'}</TableCell>
                            <TableCell>{new Date(sample.date_modification).toLocaleDateString('fr-FR')}</TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.statut} 
                                size="small" 
                                color="info"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => handleValidateSample(sample)}
                                color="primary"
                              >
                                Valider
                              </Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>ℹ️ Aucun Échantillon en Validation</Typography>
                <Typography variant="body2">
                  Il n'y a actuellement aucun échantillon avec le statut "VALIDATION_SERVICE" pour le laboratoire SAA.
                  Les échantillons doivent d'abord être analysés par les analystes avant d'apparaître ici pour validation.
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      )}

      {/* Enhanced Sample Validation Dialog with Integrated Quality Control - Optimized for Large Datasets */}
      <Dialog 
        open={validationDialog} 
        onClose={() => setValidationDialog(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#1976d2',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <VerifiedUser sx={{ fontSize: '1.8rem' }} />
              <Box>
                <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            {(selectedSample as any)?.isBatchValidation 
                    ? `🔍 Validation Lot SAA: ${selectedSample ? getBatchNumber(selectedSample) : 'N/A'}`
                    : `🔍 Validation Échantillon: ${selectedSample?.sample_id}`
            }
          </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
            {(selectedSample as any)?.isBatchValidation 
              ? `${(selectedSample as any)?.batchSamples?.length || 0} échantillons dans le lot`
              : `Type: ${selectedSample?.type_echantillon} | Client: ${selectedSample?.client?.company_name}`
            }
          </Typography>
              </Box>
            </Box>
            
            {/* Status Indicator */}
            <Box display="flex" alignItems="center" gap={2}>
              <Chip 
                label={selectedSample?.priorite || 'NORMALE'} 
                size="small" 
                color={getPriorityColor((selectedSample?.priorite || 'NORMALE') as 'NORMALE' | 'URGENTE' | 'CRITIQUE')}
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          {selectedSample && (
            <Box sx={{ mt: 1 }}>
              {(selectedSample as any)?.isBatchValidation ? (
                /* Batch Validation View - Enhanced with Complete Information */
                <Box>
                  {/* Comprehensive Batch Information Section */}
                  <Card sx={{ mb: 2, backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32', mb: 2 }}>
                        📊 Informations Complètes du Lot {selectedSample ? getBatchNumber(selectedSample) : 'N/A'}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Batch Summary */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Échantillons</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem' }}>
                              {(selectedSample as any)?.batchSamples?.length || 0}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Poids Total</Typography>
                            <Typography variant="h6" color="secondary" sx={{ fontSize: '1.2rem' }}>
                              {(() => {
                                const totalWeight = ((selectedSample as any)?.batchSamples || []).reduce((sum: number, sample: any) => {
                              const weight = sample.metadonnees?.saa_preparation?.initialWeight || 0;
                              return sum + weight;
                                }, 0);
                                return `${totalWeight.toFixed(2)} g`;
                              })()}
                          </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Client</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample?.client?.company_name || 'N/A'}
                          </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Priorité</Typography>
                            <Chip 
                              label={selectedSample?.priorite || 'NORMALE'} 
                              color={getPriorityColor(selectedSample?.priorite || 'NORMALE')} 
                              size="small" 
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Card>
                        </Grid>
                        
                        {/* Sample Context Information */}
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Projet / Site</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Projet:</strong> {selectedSample?.nom_projet || 'Non spécifié'}<br/>
                              <strong>Site:</strong> {selectedSample?.site_minier || 'Non spécifié'}
                          </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Dates</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Réception:</strong> {new Date(selectedSample?.date_reception || '').toLocaleDateString('fr-FR')}<br/>
                              <strong>Analyse:</strong> {selectedSample?.metadonnees?.saa_analysis_completed_at 
                                ? new Date(selectedSample.metadonnees.saa_analysis_completed_at).toLocaleDateString('fr-FR')
                                : 'N/A'
                              }
                            </Typography>
                          </Card>
                      </Grid>

                        {/* Elements and Methods */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                              Éléments Analysés et Informations Techniques
                        </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                              {/* ✅ NOTE: This is just for display purposes in validation dialog, not for column generation */}
                              {(selectedSample?.metadonnees?.batch_analysis_summary?.elements_analyzed || getSAABatchElementOrder([selectedSample])).map((element: string) => (
                                <Chip 
                                  key={element} 
                                  label={element} 
                                  size="small"
                                  color="primary" 
                                  variant="filled"
                                  sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                />
                          ))}
                        </Box>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              <strong>Méthode:</strong> {selectedSample?.metadonnees?.batch_analysis_summary?.detection_method || 'Spectrométrie d\'Absorption Atomique'} | 
                              <strong> Standard:</strong> {selectedSample?.metadonnees?.batch_analysis_summary?.reference_standard || 'Matériau de Référence Certifié'} | 
                              <strong> Analyses:</strong> {selectedSample?.metadonnees?.batch_analysis_summary?.total_readings || 0} lectures
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Compact Batch Summary */}
                  <Card sx={{ mb: 2, backgroundColor: '#e3f2fd' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2' }}>
                          📊 Résultats d'Analyse - Lot {selectedSample ? getBatchNumber(selectedSample) : 'N/A'}
                              </Typography>
                        
                        {/* Quality Indicators */}
                        <Box display="flex" gap={1}>
                          <Chip 
                            label={`${selectedSample?.metadonnees?.batch_analysis_summary?.below_lod_count || 0} < LOD`}
                            color={selectedSample?.metadonnees?.batch_analysis_summary?.below_lod_count > 0 ? "error" : "success"}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            label="Tous résultats OK"
                            color="success"
                            size="small"
                            variant="filled"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* 🔬 QC CORRECTION SECTION - Coefficient de Correction */}
                  <Card sx={{ mb: 2, backgroundColor: '#fff3e0', border: '2px solid #ff9800' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#e65100' }}>
                          🔬 Contrôle Qualité & Coefficient de Correction
                        </Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={showCorrectedValues}
                                onChange={(e) => setShowCorrectedValues(e.target.checked)}
                                color="warning"
                              />
                            }
                            label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Afficher valeurs corrigées</Typography>}
                          />
                          {qcLoading && <CircularProgress size={20} />}
                        </Box>
                      </Box>
                      
                      {batchQCData && Object.keys(batchQCData.qcData).length > 0 ? (
                        <Box>
                          {/* QC Summary Table */}
                          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 250 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#ffecb3' }}>
                                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>Élément</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>TQ Lecture</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#e3f2fd' }}>TQ Calculé</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>TQ (n)</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>RSD%</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#e8f5e9' }}>Val. Certifiée</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#fff3e0' }}>Récupération</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffe0b2' }}>Coef. Correction</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>Action</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(batchQCData.qcData).map(([element, qc]) => {
                                  const qcElement = qc as QCElementData;
                                  const certified = batchQCData.certifiedValues[element];
                                  const unit = certified?.unit || 'ppm';
                                  
                                  // Use pre-calculated values from loadBatchQCData
                                  const recoveryPercent = qcElement.recovery_percent;
                                  const isRecoveryOk = recoveryPercent && recoveryPercent >= 90 && recoveryPercent <= 110;
                                  const needsCorrection = recoveryPercent && (recoveryPercent < 95 || recoveryPercent > 105);
                                  
                                  return (
                                    <TableRow key={element} sx={{ 
                                      backgroundColor: needsCorrection ? alpha('#ff9800', 0.1) : 'inherit',
                                      '&:hover': { backgroundColor: alpha('#ff9800', 0.15) }
                                    }}>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{element}</TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                                        {qcElement.tq_average?.toFixed(4) || '-'}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem', backgroundColor: alpha('#2196f3', 0.05) }}>
                                        {qcElement.tq_calculated?.toFixed(4) || '-'} {unit}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{qcElement.tq_count || 0}</TableCell>
                                      <TableCell align="center" sx={{ 
                                        fontSize: '0.75rem',
                                        color: qcElement.tq_rsd > 5 ? '#f44336' : '#4caf50',
                                        fontWeight: qcElement.tq_rsd > 5 ? 'bold' : 'normal'
                                      }}>
                                        {qcElement.tq_rsd?.toFixed(1) || '-'}%
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem', backgroundColor: alpha('#4caf50', 0.05), p: 0.5 }}>
                                        <TextField
                                          type="number"
                                          size="small"
                                          value={editedCertifiedValues[element] ?? qcElement.certified_value ?? ''}
                                          onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            if (!isNaN(val) && val > 0) {
                                              handleCertifiedValueChange(element, val);
                                            }
                                          }}
                                          InputProps={{
                                            endAdornment: <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>{unit}</Typography>,
                                            sx: { fontSize: '0.75rem', py: 0 }
                                          }}
                                          inputProps={{ 
                                            step: unit === '%' ? 0.01 : 1,
                                            min: 0,
                                            style: { textAlign: 'center', padding: '4px 8px', width: '70px' }
                                          }}
                                          sx={{ 
                                            '& .MuiOutlinedInput-root': { 
                                              backgroundColor: editedCertifiedValues[element] ? alpha('#ff9800', 0.1) : 'white',
                                              '& fieldset': { borderColor: editedCertifiedValues[element] ? '#ff9800' : undefined }
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center" sx={{ 
                                        fontSize: '0.85rem', 
                                        fontWeight: 'bold',
                                        color: needsCorrection ? '#e65100' : '#4caf50',
                                        backgroundColor: needsCorrection ? alpha('#ff9800', 0.2) : alpha('#4caf50', 0.1)
                                      }}>
                                        {qcElement.correction_coefficient?.toFixed(4) || '1.0000'}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color={needsCorrection ? 'warning' : 'success'}
                                          disabled={!qcElement.correction_coefficient}
                                          onClick={() => {
                                            if (qcElement.correction_coefficient) {
                                              applyElementCorrection(
                                                element,
                                                qcElement.correction_coefficient,
                                                (selectedSample as any)?.batchSamples || []
                                              );
                                            }
                                          }}
                                          sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}
                                        >
                                          {needsCorrection ? 'Corriger' : 'OK'}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          
                          {/* Legend */}
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                            💡 <strong>Formule: Coef = Valeur Certifiée / TQ Calculé</strong> (où TQ Calculé = Lecture × V/p × VD/PD) | 
                            Récupération 90-110% = OK, 95-105% = Optimal. Cliquez "Corriger" si nécessaire.
                            {Object.keys(correctedValues).length > 0 && (
                              <Chip 
                                label={`${Object.keys(correctedValues).length} échantillons corrigés`}
                                color="warning"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </Box>
                      ) : (
                        <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                          {qcLoading ? 'Chargement des données QC...' : 'Aucune donnée QC disponible pour ce lot. Les valeurs TQ n\'ont peut-être pas été importées lors de l\'analyse.'}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  {/* SAA Formula Explanation - Updated with Dynamic Formula */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'bold', mb: 1 }}>
                      🧮 Formule SAA Utilisée: <strong>
                        {selectedSample?.metadonnees?.batch_analysis_summary?.dilution_formula || 'L × (V/p) × (vf/vp)'}
                      </strong>
                      {(() => {
                        const dilutionChoice = selectedSample?.metadonnees?.batch_analysis_summary?.dilution_choice ?? 1;
                        return (
                          <Chip 
                            label={`Choix ${dilutionChoice}`}
                            size="small"
                            color={dilutionChoice === 0 ? 'default' : dilutionChoice === 2 ? 'secondary' : 'primary'}
                            sx={{ ml: 1 }}
                          />
                        );
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      <strong>V</strong> = Volume initial après digestion (mL) | 
                      <strong>p</strong> = Poids initial échantillon (g) | 
                      <strong>vf</strong> = Volume final après dilutions (mL) | 
                      <strong>vp</strong> = Volume prélevé pour dilutions (mL)
                      {(() => {
                        const dilutionChoice = selectedSample?.metadonnees?.batch_analysis_summary?.dilution_choice ?? 1;
                        if (dilutionChoice === 2) {
                          return (
                            <span>
                              {' | '}
                              <strong>vf1</strong> = Volume final alternatif (mL) | 
                              <strong>vp1</strong> = Volume prélevé alternatif (mL)
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', mt: 1, fontStyle: 'italic' }}>
                      💡 Choix 0: Simplifié L×(V/p) | Choix 1: Complet L×(V/p)×(vf/vp) | Choix 2: Alternatif L×(V/p)×(vf1/vp1)
                    </Typography>
                  </Alert>

                  {/* Enhanced Analysis Results Table */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 1 }}>
                      <TableContainer>
                        <Table size="small" sx={{ fontSize: '0.7rem' }}>
                          <TableHead>
                            {/* FIRST ROW: Element Headers spanning across their columns */}
                            <TableRow>
                              <TableCell rowSpan={2} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                minWidth: '100px',
                                verticalAlign: 'middle'
                              }}>
                                Échantillon
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                minWidth: '80px',
                                verticalAlign: 'middle'
                              }}>
                                Poids
                                <br />
                                (g)
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                minWidth: '100px',
                                verticalAlign: 'middle'
                              }}>
                                Réf. Client
                              </TableCell>
                              
                              {/* SAA Preparation Variables */}
                              <TableCell colSpan={6} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.8rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                borderLeft: '2px solid #f57c00'
                              }}>
                                Variables Formule SAA
                              </TableCell>
                              
                              {/* Element Headers - Each element spans 4 columns */}
                              {(() => {
                                const elementsToShow = getSAABatchElementOrder((selectedSample as any)?.batchSamples || [selectedSample]);
                                
                                return elementsToShow.map((element: string, index: number) => (
                                  <TableCell 
                                    key={`${element}-group-header`} 
                                    colSpan={5}  // 🔬 Updated: 5 columns (Lecture, Dilution, Choix, Concentration, Corrigé)
                                    align="center" 
                                    sx={{ 
                                      fontWeight: 'bold', 
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontSize: '0.9rem',
                                      padding: '8px 6px',
                                      textAlign: 'center',
                                      borderLeft: '3px solid #2e7d32',
                                      borderRight: '3px solid #2e7d32'
                                    }}
                                  >
                                    {element}
                                  </TableCell>
                                ));
                              })()}
                            </TableRow>
                            
                            {/* SECOND ROW: Sub-headers for preparation variables and element data */}
                            <TableRow>
                              {/* SAA Preparation Sub-headers */}
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                V (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                p (g)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vf (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vp (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#9c27b0',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vf1 (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#9c27b0',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vp1 (mL)
                              </TableCell>
                              
                              {/* Element Sub-headers - 4 columns per element */}
                              {(() => {
                                const elementsToShow = getSAABatchElementOrder((selectedSample as any)?.batchSamples || [selectedSample]);
                                
                                return elementsToShow.flatMap((element: string) => [
                                  <TableCell key={`${element}-lecture-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#66bb6a',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px',
                                    borderLeft: '2px solid #4caf50'
                                  }}>
                                    Lecture
                                  </TableCell>,
                                  <TableCell key={`${element}-dilution-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#42a5f5',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px'
                                  }}>
                                    Dilution
                                  </TableCell>,
                                  <TableCell key={`${element}-choice-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#ab47bc',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px'
                                  }}>
                                    Choix
                                  </TableCell>,
                                  <TableCell key={`${element}-concentration-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#ffa726',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px'
                                  }}>
                                    Concentration
                                  </TableCell>,
                                  <TableCell key={`${element}-corrected-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#e91e63',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px',
                                    borderRight: '2px solid #e91e63'
                                  }}>
                                    Corrigé
                                  </TableCell>
                                ]);
                              })()}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {((selectedSample as any)?.batchSamples || [])
                              .sort((a: any, b: any) => {
                                // Smart numeric sorting
                                const aNum = parseInt(a.sample_id.replace(/\D/g, ''));
                                const bNum = parseInt(b.sample_id.replace(/\D/g, ''));
                                if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                                return a.sample_id.localeCompare(b.sample_id);
                              })
                              .map((sample: any, index: number) => (
                                <TableRow 
                                  key={sample.id}
                                  sx={{ 
                                  backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                                  '&:hover': { backgroundColor: '#f0f8ff' },
                                  '& .MuiTableCell-root': {
                                    padding: '3px 4px',
                                    fontSize: '0.7rem'
                                  }
                                  }}
                                >
                                {/* Sample ID - Ultra Compact */}
                                  <TableCell align="center">
                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                        {sample.sample_id}
                                      </Typography>
                                  </TableCell>
                                  
                                {/* Weight - Compact */}
                                  <TableCell align="center">
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                    {sample.metadonnees?.saa_preparation?.initialWeight?.toFixed(2) || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                {/* Client Reference - MISSING COLUMN ADDED */}
                                  <TableCell align="center">
                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', color: '#1976d2', textAlign: 'center' }}>
                                    {sample.external_id || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                {/* SAA Preparation Data */}
                                {(() => {
                                  const prepData = sample.metadonnees?.saa_analysis_form?.preparation_data || sample.metadonnees?.saa_preparation;
                                  const dilution = prepData?.dilutions?.[0];
                                  
                                  return (
                                    <>
                                      {/* V (mL) - Initial Volume */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {prepData?.initial_volume?.toFixed(1) || prepData?.initialVolume?.toFixed(1) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* p (g) - Initial Weight */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {prepData?.initial_weight?.toFixed(2) || prepData?.initialWeight?.toFixed(2) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* vf (mL) - Final Volume */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {dilution?.finalVolume?.toFixed(1) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* vp (mL) - Volume Taken */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {dilution?.volumeTaken?.toFixed(1) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* NEW: vf1 (mL) - Alternative Final Volume */}
                                      <TableCell align="center" sx={{ backgroundColor: '#f3e5f5' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {(() => {
                                            // Try analysis form first, fallback to original preparation data
                                            const vf1 = prepData?.vf1 || sample.metadonnees?.saa_preparation?.vf1;
                                            return vf1?.toFixed(1) || 'N/A';
                                          })()}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* NEW: vp1 (mL) - Alternative Volume Taken */}
                                      <TableCell align="center" sx={{ backgroundColor: '#f3e5f5' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {(() => {
                                            // Try analysis form first, fallback to original preparation data
                                            const vp1 = prepData?.vp1 || sample.metadonnees?.saa_preparation?.vp1;
                                            return vp1?.toFixed(1) || 'N/A';
                                          })()}
                                        </Typography>
                                      </TableCell>
                                      

                                    </>
                                  );
                                })()}
                                  
                                {/* Analysis Results - Fixed Data Extraction */}
                                  {(() => {
                                    // ✅ CRITICAL FIX: Use consistent element ordering to prevent column misalignment
                                    const elementsToShow = getSAABatchElementOrder((selectedSample as any)?.batchSamples || [selectedSample]);
                                    
                                    // ✅ FIXED: Get analysis results from the correct location
                                    const saaAnalysisReadings = sample.metadonnees?.saa_analysis_form?.analysis_readings || [];
                                    const generalAnalysisResults = sample.metadonnees?.analysis_results || [];
                                    
                                    // Use SAA analysis readings first, fallback to general analysis results
                                    const analysisData = saaAnalysisReadings.length > 0 ? saaAnalysisReadings : generalAnalysisResults;

                                      // ✅ ENHANCED: Get method code from analyses_demandees
                                      const saaMethodCode = sample.metadonnees?.analyses_demandees?.find((method: string) => 
                                        method.startsWith('AA-PRE.MO/ANA')
                                      ) || 'AA-PRE.MO/ANA/002'; // Default to multi-element SAA
                                      
                                      // ✅ METHOD-SPECIFIC DETECTION LIMITS
                                      // Import from backend configuration - using hardcoded values for frontend
                                      const getMethodSpecificLimit = (element: string, methodCode: string) => {
                                        // SAA Method Limits
                                        // 🔬 Ag and Au are displayed in g/t, all other elements in %
                                        // Detection limits converted: ppm / 10000 = % (e.g., 1 ppm = 0.0001%)
                                        const saaLimits: Record<string, { lod: number; loq: number; unit: string }> = {
                                          'Ag': { lod: 1, loq: 1, unit: 'g/t' },      // Keep g/t for silver
                                          'Au': { lod: 0.01, loq: 0.01, unit: 'g/t' }, // Keep g/t for gold
                                          'Cd': { lod: 0.00005, loq: 0.00005, unit: '%' }, // 0.5 ppm → 0.00005%
                                          'Co': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Cu': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Fe': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Mn': { lod: 0.0005, loq: 0.0005, unit: '%' },   // 5 ppm → 0.0005%
                                          'Ni': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Pb': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Zn': { lod: 0.0001, loq: 0.0001, unit: '%' }    // 1 ppm → 0.0001%
                                        };
                                        
                                        // For SAA methods, use SAA-specific limits
                                        if (methodCode.startsWith('AA-PRE.MO/ANA')) {
                                          return saaLimits[element] || { lod: 0.0001, loq: 0.0001, unit: '%' };
                                        }
                                        
                                        // Default fallback (shouldn't happen for SAA dashboard)
                                        return { lod: 0.0001, loq: 0.0001, unit: '%' };
                                      };
                                      
                                      // Build detection limits for each element based on method
                                      const detectionLimits: Record<string, { lod: number; loq: number; unit: string }> = {};
                                      elementsToShow.forEach((element: string) => {
                                        detectionLimits[element] = getMethodSpecificLimit(element, saaMethodCode);
                                      });
                                      
                                  // 🔧 FIX: Get this sample's specific elements for greyed field detection
                                  const sampleSpecificElems = getSampleSpecificElements(sample);
                                  
                                  return elementsToShow.flatMap((element: string) => {
                                    const hasElement = sampleSpecificElems.includes(element);
                                    
                                    // 🔧 FIX: Show greyed cells for elements not in this sample
                                    if (!hasElement) {
                                      return [
                                        <TableCell key={`${element}-lecture`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderLeft: '2px solid #4caf50',
                                          borderRight: '1px solid #4caf50'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-dilution`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderRight: '1px solid #2196f3'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-choice`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderRight: '1px solid #9c27b0'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-concentration`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-corrected`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderRight: '2px solid #e91e63'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>
                                      ];
                                    }
                                    
                                    const result = analysisData.find((r: any) => r.element === element);
                                      const limits = detectionLimits[element];
                                    const concentration = result?.concentration_ppm || 0;
                                    const finalConcentration = result?.concentration_final_percent || (result?.concentration_percent) || 0;
                                      
                                    return [
                                      // Raw lecture (reading from CSV import) - shows edited value if available
                                      <TableCell key={`${element}-lecture`} align="center" sx={{ 
                                        backgroundColor: editedConcentrations[sample.id]?.[element] ? '#fff3e0' : '#e8f5e8',
                                        borderLeft: '2px solid #4caf50',
                                        borderRight: '1px solid #4caf50',
                                        fontWeight: 'bold'
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: editedConcentrations[sample.id]?.[element] ? '#e65100' : 'inherit' }}>
                                          {(() => {
                                            const edited = editedConcentrations[sample.id]?.[element];
                                            if (edited) return edited.lecture.toFixed(4);
                                            const originalLecture = result?.absorbance || 0;
                                            return originalLecture > 0 ? originalLecture.toFixed(4) : 'N/A';
                                          })()}
                                        </Typography>
                                      </TableCell>,
                                      
                                      // Dilution factor
                                      <TableCell key={`${element}-dilution`} align="center" sx={{ 
                                        backgroundColor: '#e3f2fd',
                                        borderRight: '1px solid #2196f3'
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                          {(() => {
                                            // ✅ FIXED: Correct path for SAA dilution factor with multiple property name variations
                                            const dilutionFactor = sample.metadonnees?.saa_preparation?.total_dilution_factor ||
                                                                 sample.metadonnees?.saa_preparation?.totalDilutionFactor ||
                                                                 sample.metadonnees?.saa_preparation?.dilution_factor ||
                                                                 sample.metadonnees?.saa_preparation?.dilutionFactor ||
                                                                 sample.metadonnees?.saa_analysis_form?.preparation_data?.total_dilution_factor ||
                                                                 sample.metadonnees?.saa_analysis_form?.preparation_data?.totalDilutionFactor ||
                                                                 sample.metadonnees?.preparation_data?.total_dilution_factor;

                                            
                                            return dilutionFactor ? `${dilutionFactor}x` : 'N/A';
                                          })()}
                                            </Typography>
                                      </TableCell>,
                                      
                                      // ✅ NEW: Individual Element Dilution Choice
                                      <TableCell key={`${element}-choice`} align="center" sx={{ 
                                        backgroundColor: '#f3e5f5',
                                        borderRight: '1px solid #9c27b0'
                                      }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                          {(() => {
                                            const batchAnalysisSummary = sample.metadonnees?.batch_analysis_summary;
                                            const elementDilutionChoices = batchAnalysisSummary?.element_dilution_choices || {};
                                            const elementDilutionChoice = elementDilutionChoices[element] ?? 1;
                                            
                                            return (
                                              <>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                  {elementDilutionChoice}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.55rem', display: 'block' }}>
                                                  {elementDilutionChoice === 0 ? 'L×(V/p)' : 
                                                   elementDilutionChoice === 2 ? 'L×(V/p)×(vf1/vp1)' : 'L×(V/p)×(vf/vp)'}
                                                </Typography>
                                              </>
                                            );
                                          })()}
                                        </Box>
                                      </TableCell>,
                                      
                                      // Final Concentration - Editable on double-click
                                      <TableCell key={`${element}-concentration`} align="center" sx={{ 
                                        backgroundColor: editedConcentrations[sample.id]?.[element] ? '#fff3e0' : '#fff3e0',
                                        cursor: 'pointer',
                                        border: editedConcentrations[sample.id]?.[element] ? '2px solid #e65100' : undefined,
                                      }}
                                        onDoubleClick={() => {
                                          const effectivePpm = editedConcentrations[sample.id]?.[element]?.ppm ?? concentration;
                                          handleConcentrationEdit(sample.id, element, effectivePpm);
                                        }}
                                      >
                                        {editingCell?.sampleId === sample.id && editingCell?.element === element ? (
                                          <TextField
                                            value={editInputValue}
                                            onChange={(e) => handleConcentrationInputChange(e as any, sample, element)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleConcentrationConfirm(); }}
                                            onBlur={handleConcentrationConfirm}
                                            autoFocus
                                            size="small"
                                            type="number"
                                            inputProps={{ step: 0.01, min: 0, style: { textAlign: 'center', padding: '2px 4px', fontSize: '0.75rem' } }}
                                            sx={{ width: '90px' }}
                                          />
                                        ) : (
                                        (() => {
                                          const effectivePpm = editedConcentrations[sample.id]?.[element]?.ppm ?? concentration;
                                          const isEdited = !!editedConcentrations[sample.id]?.[element];
                                          if (!limits) {
                                            return <Chip label="N/A" color="default" sx={{ fontSize: '0.7rem', height: '18px' }} />;
                                          }
                                          let displayValue = effectivePpm;
                                          const displayUnit = limits.unit;
                                          const displayLod = limits.lod;
                                          if (displayUnit === '%') {
                                            displayValue = effectivePpm / 10000;
                                          }
                                          if (effectivePpm <= 0) {
                                            return <Chip label={`< ${displayLod} ${displayUnit}`} color="error" sx={{ fontSize: '0.7rem', height: '18px' }} />;
                                          }
                                          if (displayValue < displayLod) {
                                            return <Chip label={`< ${displayLod} ${displayUnit}`} color="warning" sx={{ fontSize: '0.7rem', height: '18px' }} />;
                                          }
                                          let formattedValue;
                                          if (displayUnit === '%') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else if (displayUnit === 'g/t') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else {
                                            formattedValue = displayValue.toFixed(0);
                                          }
                                          return (
                                            <Tooltip title={isEdited ? `Modifié: ${effectivePpm.toFixed(2)} ppm (Double-cliquez pour modifier)` : 'Double-cliquez pour modifier'}>
                                              <Chip label={`${formattedValue} ${displayUnit}`} color={isEdited ? 'warning' : 'success'} sx={{ fontSize: '0.7rem', height: '18px', fontWeight: 'bold' }} />
                                            </Tooltip>
                                          );
                                        })()
                                        )}
                                      </TableCell>,
                                      
                                      // 🔬 NEW: Corrected Value Column
                                      <TableCell key={`${element}-corrected`} align="center" sx={{ 
                                        backgroundColor: '#fce4ec',
                                        borderRight: '2px solid #e91e63'
                                      }}>
                                        {(() => {
                                          // Check for corrected value from multiple sources:
                                          // 1. Frontend state (after clicking "Corriger")
                                          // 2. Database (sample.metadonnees.saa_analysis.elements)
                                          const frontendCorrected = correctedValues[sample.id]?.[element];
                                          const dbCorrected = sample.metadonnees?.saa_analysis?.elements?.[element];
                                          
                                          const hasCorrectedValue = frontendCorrected || dbCorrected?.correction_applied;
                                          const correctedPpm = frontendCorrected || dbCorrected?.corrected_ppm;
                                          
                                          if (!hasCorrectedValue || !correctedPpm) {
                                            return <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>;
                                          }
                                          
                                          // Apply same unit conversion logic as concentration
                                          let displayValue = correctedPpm;
                                          const displayUnit = limits?.unit || '%';
                                          
                                          // Convert ppm to % if unit is % (divide by 10000)
                                          if (displayUnit === '%') {
                                            displayValue = correctedPpm / 10000;
                                          }
                                          // g/t is same as ppm, no conversion needed
                                          
                                          // Format the value
                                          let formattedValue;
                                          if (displayUnit === '%') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else if (displayUnit === 'g/t') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else {
                                            formattedValue = displayValue.toFixed(0);
                                          }
                                          
                                          return (
                                            <Chip 
                                              label={`${formattedValue} ${displayUnit}`} 
                                              color="secondary" 
                                              sx={{ 
                                                fontSize: '0.7rem', 
                                                height: '18px', 
                                                fontWeight: 'bold',
                                                backgroundColor: '#e91e63',
                                                color: 'white'
                                              }} 
                                            />
                                          );
                                        })()}
                                      </TableCell>
                                    ];
                                    });
                                  })()}
                                </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                  
                  {/* 🔧 ZMSM-only QA/QC card: only renders when client.company_name === 'ZMSM'.
                       Mirror of the deployed surgical patch in the bundle (prod-aligned 2026-05-15).
                       Features:
                         • Two SAMPLE-PICKER checkboxes (1st Témoin / 2nd Témoin) that auto-fill the
                           BIS 1 / BIS 2 rows from the selected sample's analytical readings.
                         • Per-element ppm → % conversion for non-Ag/Au (matches certificate output).
                         • Standard 1 / BLANK rows remain manually editable.
                       For other SAA clients this returns null and the regular flow continues unchanged. */}
                  {selectedSample?.client?.company_name === 'ZMSM' && (() => {
                    const ZMSM_DL: Record<string, { lod: string; unit: string; method: string }> = {
                      Ag: { lod: '1',    unit: 'g/t', method: 'AA-PRE.MO/ANA/002' },
                      Au: { lod: '0,05', unit: 'g/t', method: 'AA-PRE.MO/ANA/001' },
                      Cu: { lod: '0,01', unit: '%',   method: 'AA-PRE.MO/ANA/002' },
                      Fe: { lod: '0,01', unit: '%',   method: 'AA-PRE.MO/ANA/002' },
                      Pb: { lod: '0,01', unit: '%',   method: 'AA-PRE.MO/ANA/002' },
                      Zn: { lod: '0,01', unit: '%',   method: 'AA-PRE.MO/ANA/002' },
                    };
                    const ZMSM_ALLOWED: Record<string, true> = { Ag: true, Au: true, Cu: true, Fe: true, Pb: true, Zn: true };
                    const computeEls = (): string[] => {
                      const batch = (selectedSample as any)?.batchSamples || [];
                      const set = new Set<string>();
                      batch.forEach((s: any) => {
                        let sel = s?.metadonnees?.selected_elements || [];
                        if (typeof sel === 'string') {
                          try { sel = JSON.parse(sel); } catch { sel = []; }
                        }
                        (sel as string[]).forEach(el => { if (ZMSM_ALLOWED[el]) set.add(el); });
                        const routing = s?.metadonnees?.element_lab_routing || {};
                        Object.keys(routing).forEach(el => { if (ZMSM_ALLOWED[el]) set.add(el); });
                      });
                      const arr = Array.from(set);
                      if (arr.length === 0) return ['Ag', 'Cu', 'Fe', 'Pb', 'Zn'];
                      return arr.sort();
                    };
                    const loadTpl = () => {
                      const els = computeEls();
                      const mk = (label: string): ZMSMQAQCStandard => {
                        const r: ZMSMQAQCStandard = { customers_ref: label, lab_ref: '' };
                        els.forEach(el => { r[el] = ''; });
                        return r;
                      };
                      setZmsmQaqcElements(els);
                      setZmsmQaqcStandards([
                        mk('Standard 1'),
                        mk('BIS 1'),
                        mk('BIS 2'),
                        mk('BLANK (mechanical preparation)'),
                        mk('BLANK (chemical preparation)'),
                        mk('OREAS / CRM'),
                      ]);
                      setShowZmsmQaqcForm(true);
                    };
                    const closeTpl = () => {
                      setShowZmsmQaqcForm(false);
                      setZmsmQaqcStandards([]);
                      setZmsmQaqcElements([]);
                      setFirstSaaBISSampleId(null);
                      setSecondSaaBISSampleId(null);
                    };
                    const addRow = () => {
                      const els = zmsmQaqcElements.length > 0 ? zmsmQaqcElements : computeEls();
                      const r: ZMSMQAQCStandard = { customers_ref: 'Standard additionnel', lab_ref: '' };
                      els.forEach(el => { r[el] = ''; });
                      setZmsmQaqcStandards([...zmsmQaqcStandards, r]);
                    };
                    const rmRow = (idx: number) => {
                      setZmsmQaqcStandards(zmsmQaqcStandards.filter((_, i) => i !== idx));
                    };
                    const updCell = (idx: number, key: string, val: string) => {
                      const copy = zmsmQaqcStandards.slice();
                      copy[idx] = { ...copy[idx], [key]: val };
                      setZmsmQaqcStandards(copy);
                    };
                    // Resolve detection limit (number) for an element
                    const getDlLod = (el: string): number => {
                      const lodStr = ZMSM_DL[el]?.lod || '0';
                      return parseFloat(lodStr.replace(',', '.')) || 0.01;
                    };
                    // Auto-fill BIS row from a chosen sample's analytical readings.
                    // useBISFormat=true → customers_ref becomes "BIS(<first-sample-extref>)"
                    const autoFillSaaRow = (sampleId: string, rowIndex: number, useBISFormat: boolean) => {
                      const batch = (selectedSample as any)?.batchSamples || [];
                      const sample = batch.find((s: any) => s.id === sampleId);
                      if (!sample) return;
                      const ar = sample.metadonnees?.analysis_results || [];
                      const sr = sample.metadonnees?.saa_analysis_form?.analysis_readings || [];
                      const readings: any[] = ar.length > 0 ? ar : sr;
                      const getVal = (el: string): string => {
                        const lod = getDlLod(el);
                        const res = readings.find((r: any) => r.element === el);
                        if (!res) return '';
                        if (res.detection_status === 'BELOW_LOD') return `<${lod}`;
                        const rawPpm = res.concentration_ppm || 0;
                        // SAA convention: Ag/Au stay in ppm/g·t⁻¹; others convert to %
                        const val = (el === 'Ag' || el === 'Au') ? rawPpm : (rawPpm / 10000);
                        if (val === 0 || val < lod) return `<${lod}`;
                        if (el === 'Ag') return Math.round(val).toString();
                        return val.toFixed(2);
                      };
                      const clientRef = sample.external_id || sample.sample_id;
                      let custRef = clientRef;
                      if (useBISFormat && firstSaaBISSampleId) {
                        const firstSample = batch.find((s: any) => s.id === firstSaaBISSampleId);
                        const firstRef = (firstSample && (firstSample.external_id || firstSample.sample_id)) || clientRef;
                        custRef = `BIS(${firstRef})`;
                      }
                      const els = zmsmQaqcElements.length > 0 ? zmsmQaqcElements : computeEls();
                      const copy = zmsmQaqcStandards.slice();
                      const row: ZMSMQAQCStandard = { ...(copy[rowIndex] || ({} as ZMSMQAQCStandard)), customers_ref: custRef, lab_ref: sample.sample_id };
                      els.forEach((el: string) => { row[el] = getVal(el); });
                      copy[rowIndex] = row;
                      setZmsmQaqcStandards(copy);
                    };
                    const handleFirstBIS = (sampleId: string, checked: boolean) => {
                      if (checked) {
                        if (sampleId === secondSaaBISSampleId) setSecondSaaBISSampleId(null);
                        setFirstSaaBISSampleId(sampleId);
                        autoFillSaaRow(sampleId, 1, false); // row 1 = BIS 1
                      } else {
                        setFirstSaaBISSampleId(null);
                        const els = zmsmQaqcElements.length > 0 ? zmsmQaqcElements : computeEls();
                        const copy = zmsmQaqcStandards.slice();
                        const row: ZMSMQAQCStandard = { customers_ref: 'BIS 1', lab_ref: '' };
                        els.forEach((el: string) => { row[el] = ''; });
                        copy[1] = row;
                        setZmsmQaqcStandards(copy);
                      }
                    };
                    const handleSecondBIS = (sampleId: string, checked: boolean) => {
                      if (checked) {
                        if (sampleId === firstSaaBISSampleId) {
                          setFirstSaaBISSampleId(null);
                          const els0 = zmsmQaqcElements.length > 0 ? zmsmQaqcElements : computeEls();
                          const copy0 = zmsmQaqcStandards.slice();
                          const row0: ZMSMQAQCStandard = { customers_ref: 'BIS 1', lab_ref: '' };
                          els0.forEach((el: string) => { row0[el] = ''; });
                          copy0[1] = row0;
                          setZmsmQaqcStandards(copy0);
                        }
                        setSecondSaaBISSampleId(sampleId);
                        autoFillSaaRow(sampleId, 2, true); // row 2 = BIS 2
                      } else {
                        setSecondSaaBISSampleId(null);
                        const els = zmsmQaqcElements.length > 0 ? zmsmQaqcElements : computeEls();
                        const copy = zmsmQaqcStandards.slice();
                        const row: ZMSMQAQCStandard = { customers_ref: 'BIS 2', lab_ref: '' };
                        els.forEach((el: string) => { row[el] = ''; });
                        copy[2] = row;
                        setZmsmQaqcStandards(copy);
                      }
                    };
                    const batchSamplesForPicker = (selectedSample as any)?.batchSamples || [];

                    return (
                      <Card sx={{ mt: 2, backgroundColor: '#f3e5f5', border: '2px solid #9c27b0' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#6a1b9a' }}>
                              📊 QA/QC ZMSM — Tableau de Référence (BIS, Blancs, CRM)
                            </Typography>
                            {showZmsmQaqcForm ? (
                              <Button variant="outlined" size="small" color="error" onClick={closeTpl}>
                                Fermer QA/QC
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={loadTpl}
                                sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }}
                              >
                                Charger Template QA/QC ZMSM
                              </Button>
                            )}
                          </Box>
                          {showZmsmQaqcForm && zmsmQaqcStandards.length > 0 && (
                            <Box>
                              {/* 🆕 BIS-picker checkboxes (prod-aligned 2026-05-15) */}
                              <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(33,150,243,0.08)', border: '2px solid #2196f3', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                                  📊 Sélectionner le 1er Témoin (BIS 1 — ligne 2) :
                                </Typography>
                                <Box sx={{ maxHeight: 180, overflow: 'auto', mb: 2 }}>
                                  {batchSamplesForPicker.map((s: any) => (
                                    <FormControlLabel
                                      key={`first-${s.id}`}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={firstSaaBISSampleId === s.id}
                                          onChange={(_, ck) => handleFirstBIS(s.id, ck)}
                                        />
                                      }
                                      label={
                                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                                          {s.sample_id}{' '}
                                          <span style={{ color: '#888' }}>
                                            {s.external_id ? `(${s.external_id})` : ''}
                                          </span>
                                        </Typography>
                                      }
                                      sx={{ display: 'block', mb: 0 }}
                                    />
                                  ))}
                                </Box>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                                  📊 Sélectionner le 2ème Témoin (BIS 2 — ligne 3) :
                                </Typography>
                                <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                                  {batchSamplesForPicker.map((s: any) => (
                                    <FormControlLabel
                                      key={`second-${s.id}`}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={secondSaaBISSampleId === s.id}
                                          onChange={(_, ck) => handleSecondBIS(s.id, ck)}
                                        />
                                      }
                                      label={
                                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                                          {s.sample_id}{' '}
                                          <span style={{ color: '#888' }}>
                                            {s.external_id ? `(${s.external_id})` : ''}
                                          </span>
                                        </Typography>
                                      }
                                      sx={{ display: 'block', mb: 0 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', color: '#555' }}>
                                Saisissez les valeurs pour chaque standard. Les colonnes correspondent aux
                                éléments demandés sur ce lot ZMSM. Limites de détection conformes à la
                                méthode SAA. Les valeurs sont enregistrées avec la validation du lot et
                                apparaissent dans le certificat final.
                              </Typography>
                              <TableContainer component={Paper} sx={{ mb: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e1bee7', fontSize: '0.75rem' }}>Customers-Ref</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e1bee7', fontSize: '0.75rem' }}>Lab-Ref</TableCell>
                                      {zmsmQaqcElements.map(el => (
                                        <TableCell
                                          key={`hd-${el}`}
                                          align="center"
                                          sx={{ fontWeight: 'bold', backgroundColor: '#e1bee7', fontSize: '0.7rem' }}
                                        >
                                          {el}
                                          <br />
                                          {(ZMSM_DL[el] || {}).method || '-'}
                                          <br />
                                          {(ZMSM_DL[el] || {}).lod || ''}{(ZMSM_DL[el] || {}).unit ? ' ' + (ZMSM_DL[el] || {}).unit : ''}
                                        </TableCell>
                                      ))}
                                      <TableCell sx={{ backgroundColor: '#e1bee7' }}>{''}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {zmsmQaqcStandards.map((row, idx) => (
                                      <TableRow key={`rw-${idx}`}>
                                        <TableCell sx={{ padding: '4px' }}>
                                          <TextField
                                            size="small"
                                            value={row.customers_ref || ''}
                                            onChange={(ev) => updCell(idx, 'customers_ref', ev.target.value)}
                                            sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem', padding: '4px' } }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ padding: '4px' }}>
                                          <TextField
                                            size="small"
                                            value={row.lab_ref || ''}
                                            onChange={(ev) => updCell(idx, 'lab_ref', ev.target.value)}
                                            placeholder="R-XXXX-XX-XX"
                                            sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem', padding: '4px' } }}
                                          />
                                        </TableCell>
                                        {zmsmQaqcElements.map(el => (
                                          <TableCell key={`c-${idx}-${el}`} sx={{ padding: '4px' }} align="center">
                                            <TextField
                                              size="small"
                                              value={row[el] || ''}
                                              onChange={(ev) => updCell(idx, el, ev.target.value)}
                                              sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem', padding: '4px', textAlign: 'center' } }}
                                            />
                                          </TableCell>
                                        ))}
                                        <TableCell sx={{ padding: '4px' }} align="center">
                                          <Button
                                            size="small"
                                            color="error"
                                            onClick={() => rmRow(idx)}
                                            sx={{ minWidth: '28px', fontSize: '0.7rem' }}
                                          >
                                            ✕
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <Box display="flex" gap={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={addRow}
                                  sx={{ borderColor: '#9c27b0', color: '#9c27b0' }}
                                >
                                  + Ajouter une ligne
                                </Button>
                                <Typography variant="caption" sx={{ ml: 2, fontSize: '0.7rem', color: '#666', alignSelf: 'center' }}>
                                  Les valeurs sont enregistrées avec la validation du lot et apparaissent dans le certificat final.
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Batch Validation Decision Section */}
                  <Card sx={{ mt: 2, backgroundColor: '#f0f8ff' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        📝 Décision de Validation du Lot
                      </Typography>
                      
                      {/* Validation Decision Radio Buttons */}
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <RadioGroup
                          value={validationDecision}
                          onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS')}
                          row
                        >
                          <FormControlLabel
                            value="APPROVE"
                            control={<Radio color="success" />} 
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Approuver le Lot</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="REJECT"
                            control={<Radio color="error" />} 
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Cancel color="error" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Rejeter le Lot</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="REQUEST_CORRECTIONS" 
                            control={<Radio color="warning" />} 
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Edit color="warning" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Demander Corrections</Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                      
                      {/* Validation Notes */}
                      <TextField
                        label="Notes de Validation du Lot"
                        placeholder="Saisissez vos commentaires et observations de validation pour ce lot..."
                        multiline
                        rows={4}
                        fullWidth
                        value={validationNotes}
                        onChange={(e) => setValidationNotes(e.target.value)}
                        error={!validationNotes.trim()}
                        helperText={
                          !validationNotes.trim() 
                            ? "Les notes de validation sont obligatoires pour approuver ou rejeter un lot" 
                            : `${validationNotes.length}/500 caractères`
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.9rem',
                            backgroundColor: 'white'
                          }
                        }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              ) : selectedSample.metadonnees?.saa_analysis_form ? (
                /* Individual Sample Validation View - Enhanced with Complete Information */
                <Box>
                  {/* Comprehensive Sample Information Section */}
                  <Card sx={{ mb: 3, backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32', mb: 2 }}>
                        📋 Informations Complètes de l'Échantillon {selectedSample.sample_id}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Basic Sample Info */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Client</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample.client?.company_name || 'N/A'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Référence Client</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample.external_id || 'Non spécifiée'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Type Échantillon</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample.type_echantillon || 'N/A'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Priorité</Typography>
                            <Chip 
                              label={selectedSample.priorite || 'NORMALE'} 
                              color={getPriorityColor(selectedSample.priorite || 'NORMALE')} 
                              size="small" 
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Card>
                        </Grid>
                        
                        {/* Project and Site Info */}
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Projet / Site</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Projet:</strong> {selectedSample.nom_projet || 'Non spécifié'}<br/>
                              <strong>Site Minier:</strong> {selectedSample.site_minier || 'Non spécifié'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Dates Importantes</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Réception:</strong> {new Date(selectedSample.date_reception).toLocaleDateString('fr-FR')}<br/>
                              <strong>Analyse SAA:</strong> {selectedSample.metadonnees?.saa_analysis_completed_at 
                                ? new Date(selectedSample.metadonnees.saa_analysis_completed_at).toLocaleDateString('fr-FR')
                                : 'N/A'
                              }
                            </Typography>
                          </Card>
                        </Grid>
                        
                        {/* Physical Properties */}
                        {selectedSample.metadonnees && (
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                                Propriétés Physiques et Métadonnées
                              </Typography>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>Poids:</strong> {selectedSample.metadonnees.poids_kg || 'N/A'} kg
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>Sacs:</strong> {selectedSample.metadonnees.nombre_sacs || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>État:</strong> {selectedSample.metadonnees.etat_echantillon || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>Température:</strong> {selectedSample.metadonnees.temperature_reception || 'N/A'}°C
                                  </Typography>
                                </Grid>
                                {selectedSample.metadonnees.description_physique && (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                      <strong>Description:</strong> {selectedSample.metadonnees.description_physique}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Card>
                          </Grid>
                        )}
                        
                        {/* Analysis Methods */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                              Méthodes d'Analyse Demandées
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {(selectedSample.metadonnees?.analyses_demandees || selectedSample.methodes_demandees || ['SAA']).map((method: string, index: number) => (
                                <Chip 
                                  key={index} 
                                  label={method} 
                                  size="small"
                                  color="info" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Sample Analysis Results */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Résultats d'Analyse SAA</Typography>
                      
                          {/* Detection Limits Alert */}
                          {(() => {
                            const detectionLimits: Record<string, { lod: number; loq: number }> = {
                              'Fe': { lod: 0.100, loq: 0.300 },
                              'Cu': { lod: 0.050, loq: 0.150 },
                              'Pb': { lod: 0.080, loq: 0.250 },
                              'Zn': { lod: 0.030, loq: 0.100 },
                              'Mn': { lod: 0.020, loq: 0.060 },
                              'Ni': { lod: 0.040, loq: 0.120 },
                              'Cd': { lod: 0.010, loq: 0.030 },
                              'Ag': { lod: 0.005, loq: 0.015 }
                            };
                            
                            const analysisReadings = selectedSample.metadonnees.saa_analysis_form.analysis_readings || [];
                            const belowLODCount = analysisReadings.filter((reading: any) => {
                              const limits = detectionLimits[reading.element];
                              return limits && (reading.concentration_ppm || 0) < limits.lod;
                            }).length;
                            
                            const belowLOQCount = analysisReadings.filter((reading: any) => {
                              const limits = detectionLimits[reading.element];
                              const concentration = reading.concentration_ppm || 0;
                              return limits && concentration >= limits.lod && concentration < limits.loq;
                            }).length;
                            
                            if (belowLODCount > 0 || belowLOQCount > 0) {
                              return (
                                <Alert severity={belowLODCount > 0 ? "error" : "warning"} sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    ⚠️ Alertes Limites de Détection
                                  </Typography>
                                  {belowLODCount > 0 && (
                                    <Typography variant="body2">
                                      • {belowLODCount} résultat(s) en dessous de la LOD (Limite de Détection) - Validation critique requise
                                    </Typography>
                                  )}
                                  {belowLOQCount > 0 && (
                                    <Typography variant="body2">
                                      • {belowLOQCount} résultat(s) entre LOD et LOQ (incertitude accrue) - Attention requise
                                    </Typography>
                                  )}
                                </Alert>
                              );
                            }
                            return null;
                          })()}

                          {/* Preparation Data */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Données de Préparation SAA</Typography>
                            
                            {/* Basic Preparation Data */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Poids initial (p):</strong> {selectedSample.metadonnees.saa_analysis_form.preparation_data?.initial_weight || 'N/A'} g
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Méthode digestion:</strong> {selectedSample.metadonnees.saa_analysis_form.preparation_data?.digestion_method || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Volume initial (V):</strong> {selectedSample.metadonnees.saa_analysis_form.preparation_data?.initial_volume || 'N/A'} mL
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Facteur dilution total:</strong> {selectedSample.metadonnees?.saa_preparation?.total_dilution_factor || selectedSample.metadonnees?.saa_preparation?.totalDilutionFactor || selectedSample.metadonnees?.saa_preparation?.dilution_factor || selectedSample.metadonnees?.saa_preparation?.dilutionFactor || selectedSample.metadonnees?.saa_analysis_form?.preparation_data?.total_dilution_factor || 'N/A'}x
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Dilution Data (vf and vp) */}
                            {selectedSample.metadonnees.saa_analysis_form.preparation_data?.dilutions && 
                             selectedSample.metadonnees.saa_analysis_form.preparation_data.dilutions.length > 0 && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                                <Typography variant="body2" color="primary" fontWeight="bold" gutterBottom>
                                  Variables Formule SAA: L × (V/p) × (vf/vp)
                                </Typography>
                                <Grid container spacing={2}>
                                  {selectedSample.metadonnees.saa_analysis_form.preparation_data.dilutions.map((dilution: any, index: number) => (
                                    <React.Fragment key={index}>
                                      <Grid item xs={3}>
                                        <Typography variant="body2">
                                          <strong>Volume final (vf):</strong> {dilution.finalVolume?.toFixed(1) || 'N/A'} mL
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <Typography variant="body2">
                                          <strong>Volume prélevé (vp):</strong> {dilution.volumeTaken?.toFixed(1) || 'N/A'} mL
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <Typography variant="body2">
                                          <strong>Facteur dilution:</strong> {dilution.dilutionFactor?.toFixed(1) || 'N/A'}x
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <Typography variant="body2" color="text.secondary">
                                          <strong>Dilution #{index + 1}</strong>
                                        </Typography>
                                      </Grid>
                                    </React.Fragment>
                                  ))}
                                </Grid>
                              </Box>
                            )}

                            {/* Formula Explanation */}
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <strong>Formule SAA:</strong> Concentration = L × (V/p) × (vf/vp) 
                                <br />
                                <strong>Conversion:</strong> % = ppm ÷ 10,000
                              </Typography>
                            </Alert>
                          </Box>

                          {/* Analysis Results */}
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Élément</TableCell>
                                  <TableCell align="right">Longueur d'onde (nm)</TableCell>
                                  <TableCell align="right">Absorbance</TableCell>
                                  <TableCell align="right">Concentration (ppm)</TableCell>
                                  <TableCell align="right">Concentration finale</TableCell>
                                  <TableCell align="right">Incertitude</TableCell>
                                  <TableCell align="center">Statut QC</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedSample.metadonnees.saa_analysis_form.analysis_readings?.map((reading: any) => (
                                  <TableRow key={`${selectedSample.id}-${reading.element}`}>
                                    <TableCell>
                                      <Chip label={reading.element} size="small" color="primary" />
                                    </TableCell>
                                    <TableCell align="right">
                                      {reading.instrument_settings?.wavelength || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {reading.absorbance?.toFixed(4) || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {reading.concentration_ppm?.toFixed(3) || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {(() => {
                                        // Detection limits for proper display
                                        const detectionLimits: Record<string, { lod: number; loq: number }> = {
                                          'Fe': { lod: 0.100, loq: 0.300 },
                                          'Cu': { lod: 0.050, loq: 0.150 },
                                          'Pb': { lod: 0.080, loq: 0.250 },
                                          'Zn': { lod: 0.030, loq: 0.100 },
                                          'Mn': { lod: 0.020, loq: 0.060 },
                                          'Ni': { lod: 0.040, loq: 0.120 },
                                          'Cd': { lod: 0.010, loq: 0.030 },
                                          'Ag': { lod: 0.005, loq: 0.015 }
                                        };
                                        
                                        const limits = detectionLimits[reading.element];
                                        const concentration = reading.concentration_ppm || 0;
                                        const finalConcentration = reading.concentration_final_percent || 0;
                                        
                                        if (limits && concentration < limits.lod) {
                                          return (
                                            <Typography variant="body2" fontWeight={600} color="error">
                                              &lt; LOD
                                            </Typography>
                                          );
                                        } else if (limits && concentration < limits.loq) {
                                          return (
                                    <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ fontSize: '0.65rem' }}>
                                              {reading.element === 'Fe' 
                                                ? `${finalConcentration.toFixed(4)}%`
                                        : `${(finalConcentration * 10000).toFixed(1)}`
                                              }
                                            </Typography>
                                          );
                                        } else {
                                          return (
                                    <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.65rem' }}>
                                              {reading.element === 'Fe' 
                                                ? `${finalConcentration.toFixed(4)}%`
                                        : `${(finalConcentration * 10000).toFixed(1)}`
                                              }
                                            </Typography>
                                          );
                                        }
                                      })()}
                                    </TableCell>
                                    <TableCell align="right">
                                      ±{reading.uncertainty?.toFixed(3) || '0.000'}
                                    </TableCell>
                                    <TableCell align="center">
                                      {(() => {
                                        // Detection limits for SAA elements (from our quality standards)
                                        const detectionLimits: Record<string, { lod: number; loq: number }> = {
                                          'Fe': { lod: 0.100, loq: 0.300 },
                                          'Cu': { lod: 0.050, loq: 0.150 },
                                          'Pb': { lod: 0.080, loq: 0.250 },
                                          'Zn': { lod: 0.030, loq: 0.100 },
                                          'Mn': { lod: 0.020, loq: 0.060 },
                                          'Ni': { lod: 0.040, loq: 0.120 },
                                          'Cd': { lod: 0.010, loq: 0.030 },
                                          'Ag': { lod: 0.005, loq: 0.015 }
                                        };
                                        
                                        const limits = detectionLimits[reading.element];
                                        const concentration = reading.concentration_ppm || 0;
                                        
                                        if (!limits) {
                                          return <Chip label="N/A" color="default" size="small" />;
                                        }
                                        
                                        if (concentration < limits.lod) {
                                          return <Chip label="&lt; LOD" color="error" size="small" />;
                                        } else if (concentration < limits.loq) {
                                          return <Chip label="LOD-LOQ" color="warning" size="small" />;
                                        } else {
                                          return <Chip label="PASSED" color="success" size="small" />;
                                        }
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Calibration Info */}
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Information de Calibration</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Coefficient R² : {selectedSample.metadonnees.saa_analysis_form.instrument_status?.r_squared?.toFixed(4) || '0.9995'} 
                              {(selectedSample.metadonnees.saa_analysis_form.instrument_status?.r_squared || 0.9995) >= 0.995 
                                ? ' ✓ Conforme' 
                                : ' ✗ Non conforme'
                              }
                            </Typography>
                          </Box>
                    </CardContent>
                  </Card>
                  
                  {/* Validation Decision Section */}
                  <Card sx={{ mt: 2, backgroundColor: '#f0f8ff' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        📝 Décision de Validation
                    </Typography>
                    
                      {/* Validation Decision Radio Buttons */}
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <RadioGroup
                        value={validationDecision}
                          onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS')}
                          row
                      >
                        <FormControlLabel
                          value="APPROVE"
                            control={<Radio color="success" />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Approuver</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="REJECT"
                            control={<Radio color="error" />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Cancel color="error" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Rejeter</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                            value="REQUEST_CORRECTIONS" 
                            control={<Radio color="warning" />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Edit color="warning" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Demander Corrections</Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    
                      {/* Validation Notes */}
                    <TextField
                        label="Notes de Validation"
                        placeholder="Saisissez vos commentaires et observations de validation..."
                      multiline
                      rows={4}
                        fullWidth
                      value={validationNotes}
                      onChange={(e) => setValidationNotes(e.target.value)}
                      error={!validationNotes.trim()}
                        helperText={
                          !validationNotes.trim() 
                            ? "Les notes de validation sont obligatoires pour approuver ou rejeter un échantillon" 
                            : `${validationNotes.length}/500 caractères`
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.9rem',
                            backgroundColor: 'white'
                          }
                        }}
                    />
                    </CardContent>
                  </Card>
                  </Box>
              ) : (
                <Alert severity="warning">
                  Aucune donnée d'analyse SAA disponible pour cet échantillon.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000
        }}>
          <Button 
            onClick={() => setValidationDialog(false)} 
            startIcon={<Cancel />}
            sx={{ fontSize: '0.8rem' }}
          >
            Annuler
          </Button>
          
          {(selectedSample?.metadonnees?.saa_analysis_form || 
            (selectedSample as any)?.isBatchValidation) && (
            <Button
              variant="outlined"
              color="info"
              startIcon={generatingPartialCert ? <CircularProgress size={16} /> : <Description />}
              onClick={handleGeneratePartialCertificate}
              disabled={generatingPartialCert}
              sx={{ fontSize: '0.8rem' }}
            >
              {generatingPartialCert ? 'Generation en cours...' : 'Certificat Partiel SAA'}
            </Button>
          )}
          
          {/* Enable validation button for both individual samples and batch samples */}
          {(selectedSample?.metadonnees?.saa_analysis_form || 
            selectedSample?.metadonnees?.batch_analysis_summary ||
            (selectedSample as any)?.isBatchValidation) && (
            <Button 
              variant="contained" 
              onClick={handleCompleteValidation}
              startIcon={<Save />}
              disabled={!validationNotes.trim()}
              color={validationDecision === 'APPROVE' ? 'success' : 'error'}
              sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
              title={!validationNotes.trim() ? "Veuillez saisir des notes de validation pour activer ce bouton" : ""}
            >
              {(selectedSample as any)?.isBatchValidation 
                ? (validationDecision === 'APPROVE' ? 'Approuver le Lot' : 'Rejeter le Lot')
                : (validationDecision === 'APPROVE' ? 'Approuver l\'Échantillon' : 
                   (validationDecision === 'REJECT' ? 'Rejeter l\'Échantillon' : 'Demander Corrections'))
              }
              {!validationNotes.trim() && (
                <Typography variant="caption" sx={{ ml: 1, opacity: 0.7, fontSize: '0.7rem' }}>
                  (Notes requises)
                </Typography>
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default SAASupervisorDashboard; 